import React, { useState, useRef, useEffect } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import chroma from 'chroma-js';
import axios from 'axios';
import { mapConfigurationsAPI } from '../../services/api'; // Adjust path as necessary
import { getApiUrl } from '../../utils/auth';
import { 
  initialLayerConfigurations,
  // createClassBreaks as createClassBreaksFromConfig // Only if used for generic fallback
} from './mapConfig'; // Adjust path as necessary
import { getCompFieldFormat } from './heatMapGenerator'; // Import field format detector
import MapGenerationLoader from './MapGenerationLoader'; // Loading overlay for heat/dot density maps

/**
 * Intelligently rounds numbers based on their magnitude
 * for better display in visualizations.
 *
 * @param {number} value - Value to round
 * @param {boolean} isYearValue - Whether this appears to be a year value
 * @return {number} - Rounded value
 */
const smartRound = (value, isYearValue = false) => {
  if (typeof value !== 'number' || isNaN(value) || value === Infinity) return value;

  // For year values (1900-2100), use minimal rounding
  if (isYearValue || (value >= 1900 && value <= 2100)) {
    return Math.round(value);
  }

  // For small values, preserve more precision
  const absValue = Math.abs(value);
  if (absValue < 1) return Math.round(value * 100) / 100;
  if (absValue < 10) return Math.round(value * 10) / 10;
  if (absValue < 100) return Math.round(value);
  if (absValue < 1000) return Math.round(value / 10) * 10;
  if (absValue < 10000) return Math.round(value / 100) * 100;
  return Math.round(value / 1000) * 1000;
};  

/**
 * Determines the optimal number of breaks based on number of UNIQUE values
 * using exact thresholds from specifications.
 *
 * @param {number} uniqueValueCount - Number of unique values in the data
 * @return {number} - Optimal number of breaks (1-10)
 */
const determineBreakCountByUniqueValues = (uniqueValueCount) => {
  if (uniqueValueCount <= 1) return 1;
  if (uniqueValueCount <= 5) return 2;
  if (uniqueValueCount <= 9) return 3;
  if (uniqueValueCount <= 16) return 4;
  if (uniqueValueCount <= 25) return 5;
  if (uniqueValueCount <= 30) return 6;
  if (uniqueValueCount <= 42) return 7;
  if (uniqueValueCount <= 56) return 8;
  if (uniqueValueCount <= 81) return 9;
  return 10; // For 82+ unique values
};

/**
 * Formats column names into user-friendly legend titles
 * Maps specific field names to their proper display formats for comp maps
 *
 * @param {string} columnName - The column name from the data
 * @returns {string} Formatted legend title
 */
const formatLegendTitle = (columnName) => {
  if (!columnName) return 'Comparable Properties';

  const normalized = columnName.replace(/[\s.]/g, '').toLowerCase();

  // Zip Code
  if (normalized === 'zip') return 'Zip Code';

  // Total Units -> Building Units
  if (normalized === 'totalunits') return 'Building Units';

  // Occupancy (no % in title, % shown in values)
  if (normalized === 'occupancy') return 'Occupancy';

  // Year fields
  if (normalized === 'yearbuilt') return 'Year Built';
  if (normalized === 'yearreno') return 'Year Renovated';

  // Average Size
  if (normalized === 'avgsize') return 'Average Unit Size (SF)';

  // Rent fields
  if (normalized === 'avgbaserent') return 'Average Base Rent';
  if (normalized === 'avgbasepsf') return 'Average Base Rent PSF';
  if (normalized === 'avgconc') return 'Average Concession';
  if (normalized === 'avgnetrent' || normalized === 'netrent') return 'Average Net Rent';
  if (normalized === 'avgnetpsf' || normalized === 'netpsf') return 'Average Net Rent PSF';

  // Percentage unit mix fields
  if (normalized === 'percstudio' || normalized === 'studio' || normalized === '%studio') return 'Percent Studio Units';
  if (normalized === 'percopen1bed' || normalized === 'open1bed' || normalized === '%open1-bed') return 'Percent Open 1-Bed Units';
  if (normalized === 'perc1bed' || normalized === '1bed' || normalized === '%1-bed') return 'Percent 1-Bed Units';
  if (normalized === 'perc2bed' || normalized === '2bed' || normalized === '%2-bed') return 'Percent 2-Bed Units';
  if (normalized === 'perc3bed' || normalized === '3bed' || normalized === '%3-bed') return 'Percent 3-Bed Units';
  if (normalized === 'perc4bed' || normalized === '4bed' || normalized === '%4-bed') return 'Percent 4-Bed Units';
  if (normalized === 'percother' || normalized === 'other' || normalized === '%other') return 'Percent Other Units';

  // Text fields
  if (normalized === 'management') return 'Management';
  if (normalized === 'producttype') return 'Product Type';
  if (normalized === 'maplabel') return 'Map Label';

  // Other numeric fields
  if (normalized === 'elevation') return 'Elevation (Stories)';
  if (normalized === 'walkscore') return 'Walk Score';

  // Default: return the column name as-is
  return columnName;
};

// 7-level color scheme for heat maps (with transparency)
const COLOR_SCHEME = {
    level1: [128, 0, 128, 0.35], // Purple
    level2: [0, 0, 139, 0.20], // Dark blue
    level3: [135, 206, 235, 0.35], // Sky blue
    level4: [144, 238, 144, 0.35], // Light green
    level5: [255, 255, 144, 0.35], // Light yellow
    level6: [255, 165, 0, 0.15], // Orange
    level7: [255, 99, 71, 0.15], // Salmon red
};

// Color scheme for geography maps - Heatmap style (Light Purple to Red)
const GEOGRAPHY_COLOR_SCHEME = {
  name: 'Heatmap',
  colors: [
    [200, 180, 220, 0.5],    // Light Purple
    [0, 0, 139, 0.5],        // Dark Blue
    [135, 206, 250, 0.5],    // Light Blue
    [34, 139, 34, 0.5],      // Green
    [255, 255, 0, 0.5],      // Yellow
    [255, 165, 0, 0.5],      // Orange
    [255, 0, 0, 0.5],        // Red
  ]
};

// TCG Color palette for comp maps (with full opacity)
// Red is always break 1 (highest value), colors added sequentially for more breaks
// 3 breaks: Red, Orange, Yellow | 5 breaks: Red, Orange, Yellow, Green, Cyan | etc.
const TCG_COLORS_ARRAY = [
  [191, 0, 0, 0.8],       // 1. TCG Red Dark (HIGHEST - always break 1)
  [255, 122, 13, 0.8],    // 2. TCG Orange Dark
  [248, 242, 0, 0.8],     // 3. TCG Yellow Dark
  [0, 191, 44, 0.8],      // 4. TCG Green Dark
  [0, 155, 155, 0.8],     // 5. TCG Cyan Dark
  [0, 51, 128, 0.8],      // 6. TCG Blue Dark
  [92, 0, 184, 0.8],      // 7. TCG Purple Dark
  [214, 0, 158, 0.8],     // 8. Pink Dark
  [148, 112, 60, 0.8],    // 9. Brown Dark
  [174, 170, 170, 0.8]    // 10. Carbon Gray Light (LOWEST)
];

// Distinct colors for categorical/unique value classification
const CATEGORICAL_COLORS_ARRAY = [
  [191, 0, 0, 0.8],       // Red
  [0, 51, 128, 0.8],      // Blue
  [0, 191, 44, 0.8],      // Green
  [255, 122, 13, 0.8],    // Orange
  [92, 0, 184, 0.8],      // Purple
  [0, 155, 155, 0.8],     // Cyan
  [214, 0, 158, 0.8],     // Pink
  [248, 242, 0, 0.8],     // Yellow
  [148, 112, 60, 0.8],    // Brown
  [174, 170, 170, 0.8],   // Gray
  [0, 100, 0, 0.8],       // Dark Green
  [70, 130, 180, 0.8],    // Steel Blue
  [255, 20, 147, 0.8],    // Deep Pink
  [139, 69, 19, 0.8],     // Saddle Brown
  [75, 0, 130, 0.8],      // Indigo
];

/**
 * Gets the appropriate color for each break using the 7-level color scheme
 * @param {number} index - Index of the break (0-based)
 * @param {number} totalBreaks - Total number of breaks (1-10)
 * @param {string} returnFormat - 'array' for [r,g,b,a] or 'string' for CSS
 * @return {Array|string} - Color in requested format
 */
const getBreakColor = (index, totalBreaks, returnFormat = 'array') => {
  // Define color mappings for different break counts using the 7-level scheme
  const colorMappings = {
    1: ['level1'],
    2: ['level1', 'level7'],
    3: ['level1', 'level4', 'level7'],
    4: ['level1', 'level3', 'level5', 'level7'],
    5: ['level1', 'level2', 'level4', 'level6', 'level7'],
    6: ['level1', 'level2', 'level3', 'level5', 'level6', 'level7'],
    7: ['level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7'],
    8: ['level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7', 'level1'],
    9: ['level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7', 'level1', 'level2'],
    10: ['level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7', 'level1', 'level2', 'level3']
  };

  // Validate inputs and get appropriate color mapping
  const validBreakCount = Math.max(1, Math.min(10, Math.floor(totalBreaks)));
  const validIndex = Math.max(0, Math.min(validBreakCount - 1, index));
  const colorKeys = colorMappings[validBreakCount] || colorMappings[7];
  const colorKey = colorKeys[validIndex];

  // Get color array from the color scheme (RGBA format)
  const colorArray = COLOR_SCHEME[colorKey] || COLOR_SCHEME.level1;
  
  // Return in requested format
  return returnFormat === 'string' ? formatColorForDisplay([...colorArray]) : [...colorArray];
};

/**
 * Creates class breaks for data visualization using the 7-level color scheme
 * @param {Array} breakPoints - Array of break point objects with min/max values
 * @param {Array} labels - Array of labels for each break
 * @return {Array} - Array of class break objects with new color scheme
 */
const createClassBreaks = (breakPoints, labels) => {
  return breakPoints.map((point, index) => {
    // Get color using the new color scheme
    const colorArray = getBreakColor(index, breakPoints.length, 'array');
    
    return {
      minValue: point.min === undefined ? -Infinity : point.min,
      maxValue: point.max === undefined ? Infinity : point.max,
      symbol: {
        type: "simple-fill",
        color: [...colorArray], // Use new color scheme
        outline: {
          color: [50, 50, 50, 0.2],
          width: "0.5px",
        },
      },
      label: labels[index],
      // Add metadata to help preserve the new color scheme
      preserveOpacity: true,
      originalOpacity: colorArray[3],
      hasCustomOpacities: true,
      colorSchemeLevel: `level${Math.min(index + 1, 7)}` // Track which level was used
    };
  });
};

// Helper function to get predefined break ranges based on total breaks
/**
 * Returns precise break ranges for all break levels (1-10)
 * based on the specifications.
 * 
 * @param {number} totalBreaks - Total number of breaks (1-10)
 * @return {Array} - Array of min/max range objects
 */
const getBreakRanges = (totalBreaks) => {
  // Complete break ranges for all levels 1-10
  const breakRanges = {
    1: [
      {min: 0, max: 1}
    ],
    2: [
      {min: 0, max: 1},
      {min: 2, max: 5}
    ],
    3: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9}
    ],
    4: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16}
    ],
    5: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16},
      {min: 17, max: 25}
    ],
    6: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16},
      {min: 17, max: 25},
      {min: 26, max: 30}
    ],
    7: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16},
      {min: 17, max: 25},
      {min: 26, max: 30},
      {min: 31, max: 42}
    ],
    8: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16},
      {min: 17, max: 25},
      {min: 26, max: 30},
      {min: 31, max: 42},
      {min: 43, max: 56}
    ],
    9: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16},
      {min: 17, max: 25},
      {min: 26, max: 30},
      {min: 31, max: 42},
      {min: 43, max: 56},
      {min: 57, max: 81}
    ],
    10: [
      {min: 0, max: 1},
      {min: 2, max: 5},
      {min: 6, max: 9},
      {min: 10, max: 16},
      {min: 17, max: 25},
      {min: 26, max: 30},
      {min: 31, max: 42},
      {min: 43, max: 56},
      {min: 57, max: 81},
      {min: 82, max: 200}
    ]
  };
  
  // Validate and return the appropriate break range
  const validBreakCount = Math.max(1, Math.min(10, Math.floor(totalBreaks)));
  return breakRanges[validBreakCount] || breakRanges[10];
};

/**
 * Calculates evenly distributed break points based on actual data values.
 *
 * @param {Array<number>} values - Sorted array of numeric values
 * @param {number} breakCount - Number of breaks to generate
 * @param {boolean} roundToTens - Whether to round break values to nearest 10
 * @return {Array<{min: number, max: number}>} - Array of min/max range objects
 */
const calculateDataDrivenBreaks = (values, breakCount, roundToTens = false) => {
  if (!values || values.length === 0 || !breakCount) {
    return [];
  }

  // Sort values if not already sorted
  const sortedValues = [...values].sort((a, b) => a - b);
  const min = sortedValues[0];
  const max = sortedValues[sortedValues.length - 1];

  // Handle special case where all values are the same
  if (min === max) {
    return [{ min, max }];
  }

  // Get unique values
  const uniqueValues = [...new Set(sortedValues)];

  // Ensure we don't create more breaks than unique values
  const actualBreakCount = Math.min(breakCount, uniqueValues.length);

  // Detect if values look like years (common in real estate data)
  const isYearData = min >= 1900 && max <= 2100;

  const breaks = [];

  // Use quantile-based breaks for better distribution
  const quantileSize = sortedValues.length / actualBreakCount;

  for (let i = 0; i < actualBreakCount; i++) {
    // Calculate quantile positions
    const startIdx = Math.floor(i * quantileSize);
    const endIdx = i === actualBreakCount - 1
      ? sortedValues.length - 1
      : Math.floor((i + 1) * quantileSize) - 1;

    // Get the actual values at these positions
    let rangeMin = sortedValues[startIdx];
    let rangeMax = sortedValues[Math.min(endIdx, sortedValues.length - 1)];

    // For better visual clarity, adjust to use actual data values as boundaries
    if (i > 0) {
      // Ensure no overlap with previous break
      const prevMax = breaks[i - 1].max;
      // Find the next unique value after prevMax
      const nextUniqueIdx = sortedValues.findIndex(v => v > prevMax);
      if (nextUniqueIdx !== -1) {
        rangeMin = sortedValues[nextUniqueIdx];
      }
    }

    breaks.push({
      min: rangeMin,
      max: rangeMax
    });
  }

  // Clean up any remaining overlaps
  for (let i = 0; i < breaks.length - 1; i++) {
    if (breaks[i].max >= breaks[i + 1].min) {
      // For year data, use the exact year as boundary
      if (isYearData) {
        breaks[i].max = breaks[i + 1].min - 1;
      } else {
        // Use midpoint without rounding
        const midpoint = (breaks[i].max + breaks[i + 1].min) / 2;
        breaks[i].max = midpoint;
        breaks[i + 1].min = midpoint;
      }
    }
  }

  // Ensure the last break includes the maximum value
  if (breaks.length > 0) {
    breaks[breaks.length - 1].max = max;
  }

  // Apply rounding to tens if requested (for Avg. Size, Avg. Base Rent, Avg. Net Rent)
  if (roundToTens && breaks.length > 0) {
    for (let i = 0; i < breaks.length; i++) {
      // Round min and max to nearest 10
      breaks[i].min = Math.round(breaks[i].min / 10) * 10;
      breaks[i].max = Math.round(breaks[i].max / 10) * 10;
    }
    // Clean up any overlaps after rounding
    for (let i = 0; i < breaks.length - 1; i++) {
      if (breaks[i].max >= breaks[i + 1].min) {
        // Adjust to eliminate overlap by moving the boundary
        breaks[i + 1].min = breaks[i].max + 10;
      }
    }
  }

  return breaks;
};

const generateTableDrivenClassBreaks = (data, valueColumn, baseSymbolStyle = {}, customBreakCount = null, valueFormat = null) => {
  // Validate inputs
  if (!data || data.length === 0 || !valueColumn) {
    console.warn("generateTableDrivenClassBreaks: Missing data or valueColumn.");
    return null;
  }

  // Detect if this is an Elevation/Stories column that needs "s" suffix in legend
  const columnLower = valueColumn.toLowerCase();
  const normalizedColumn = valueColumn.replace(/[\s.]/g, '').toLowerCase();
  const isStoriesColumn = columnLower === 'elevation' || columnLower === 'stories' || columnLower === 'floors';
  // Detect columns that should have break values rounded to nearest 10
  const shouldRoundToTens = normalizedColumn === 'avgsize' || normalizedColumn === 'avgbaserent' || normalizedColumn === 'avgnetrent';
  let detectedStoriesSuffix = false;

  // Extract numeric values from data
  // Handles common suffixes like "s" for stories (e.g., "4s" → 4)
  const values = data.map(item => {
    let rawValue = item[valueColumn];
    if (rawValue === null || rawValue === undefined || rawValue === '') return NaN;

    // Convert to string and strip common suffixes (s for stories, ft, etc.)
    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      // Detect if values have "s" suffix (stories)
      if (/^\d+s$/i.test(trimmed)) {
        detectedStoriesSuffix = true;
      }
      rawValue = trimmed.replace(/s$/i, '').replace(/ft$/i, '').replace(/stories?$/i, '');
    }

    const num = Number(rawValue);
    return isNaN(num) ? NaN : num;
  }).filter(val => !isNaN(val));

  // Handle special case with no valid numeric values - try categorical
  if (values.length === 0) {
    console.log(`generateTableDrivenClassBreaks: No numeric data in '${valueColumn}', trying categorical classification.`);

    // Extract unique categorical values
    const categoricalValues = data
      .map(item => {
        const rawValue = item[valueColumn];
        if (rawValue === null || rawValue === undefined || rawValue === '') return null;
        return String(rawValue).trim();
      })
      .filter(val => val !== null);

    const uniqueCategories = [...new Set(categoricalValues)].sort();

    if (uniqueCategories.length === 0) {
      console.warn(`generateTableDrivenClassBreaks: No valid data in '${valueColumn}'.`);
      return null;
    }

    // Generate unique value breaks for categorical data
    console.log(`Generating categorical breaks for ${uniqueCategories.length} unique values in '${valueColumn}'`);

    const categoricalBreaks = uniqueCategories.map((category, index) => {
      const colorArray = CATEGORICAL_COLORS_ARRAY[index % CATEGORICAL_COLORS_ARRAY.length];

      return {
        value: category, // Store the actual category value for unique value renderer
        minValue: index, // For compatibility with class breaks renderer
        maxValue: index,
        label: category,
        isCategorical: true, // Flag to indicate this is categorical data
        isTextField: true, // Flag for text field matching in mapLayerUtils
        symbol: {
          type: "simple-marker",
          style: baseSymbolStyle.style || 'circle',
          color: [...colorArray],
          size: baseSymbolStyle.size || 6,
          outline: {
            color: baseSymbolStyle.outline?.color || '#000000',
            width: Number(baseSymbolStyle.outline?.width) || 1
          }
        }
      };
    });

    return categoricalBreaks;
  }

  // Auto-apply "s" suffix for stories columns if detected or column name matches
  const effectiveValueFormat = valueFormat || {};
  if ((isStoriesColumn || detectedStoriesSuffix) && !effectiveValueFormat.suffix) {
    effectiveValueFormat.suffix = 's';
    effectiveValueFormat.decimals = 0;
    effectiveValueFormat.noCommas = true;
    console.log(`[generateTableDrivenClassBreaks] Auto-detected stories column "${valueColumn}", adding "s" suffix to legend`);
  }

  // Helper function to format a value with prefix/suffix
  const formatValue = (val) => {
    const decimals = effectiveValueFormat?.decimals !== undefined ? effectiveValueFormat.decimals : 0;
    const prefix = effectiveValueFormat?.prefix || '';
    const suffix = effectiveValueFormat?.suffix || '';
    const multiplier = effectiveValueFormat?.multiplier || 1;
    const noCommas = effectiveValueFormat?.noCommas || false;

    const adjustedVal = val * multiplier;
    const roundedVal = Number(adjustedVal.toFixed(decimals));

    // Format number - skip commas for years (noCommas flag)
    let formattedNum;
    if (noCommas) {
      formattedNum = roundedVal.toFixed(decimals).replace(/\.0+$/, '');
    } else {
      formattedNum = roundedVal.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      });
    }

    return `${prefix}${formattedNum}${suffix}`;
  };

  // Helper function to get one unit less than a value (for clearer break labels)
  // e.g., $1.30 becomes $1.29, 100 becomes 99
  const getOneUnitLess = (val) => {
    const decimals = effectiveValueFormat?.decimals !== undefined ? effectiveValueFormat.decimals : 0;
    const multiplier = effectiveValueFormat?.multiplier || 1;
    const adjustedVal = val * multiplier;
    const unitValue = Math.pow(10, -decimals); // 0.01 for 2 decimals, 1 for 0 decimals
    return (adjustedVal - unitValue) / multiplier;
  };

  // Handle special case with single value
  if (values.length === 1) {
    const isYearData = values[0] >= 1900 && values[0] <= 2100;
    const singleValue = smartRound(values[0], isYearData);
    const colorArray = getBreakColor(0, 1); // Use new color scheme

    return [{
      minValue: singleValue,
      maxValue: singleValue,
      label: formatValue(singleValue),
      symbol: {
        type: "simple-marker",
        style: baseSymbolStyle.style || 'circle',
        color: [...colorArray], // Use new color scheme
        size: baseSymbolStyle.size || 6,
        outline: {
          color: baseSymbolStyle.outline?.color || '#000000',
          width: Number(baseSymbolStyle.outline?.width) || 1
        }
      },
      preserveOpacity: true,
      originalOpacity: colorArray[3],
      hasCustomOpacities: true,
      colorSchemeLevel: 'level1'
    }];
  }

  // Determine optimal break count based on number of UNIQUE values
  const uniqueValues = new Set(values);
  const uniqueValueCount = uniqueValues.size;
  const optimalBreakCount = customBreakCount !== null ?
    customBreakCount : determineBreakCountByUniqueValues(uniqueValueCount);

  console.log(`Data has ${data.length} points with ${uniqueValueCount} unique values, using ${optimalBreakCount} breaks`);
  if (effectiveValueFormat && Object.keys(effectiveValueFormat).length > 0) {
    console.log(`Applying value format to legend labels:`, effectiveValueFormat);
  }

  // Generate data-driven breaks based on the actual values
  const breakRanges = calculateDataDrivenBreaks(values, optimalBreakCount, shouldRoundToTens);
  const breaks = [];

  // Create break objects with appropriate colors and labels using new color scheme
  for (let i = 0; i < breakRanges.length; i++) {
    const range = breakRanges[i];
    // Get color as RGBA array using new color scheme
    const colorArray = getBreakColor(i, breakRanges.length);
    const colorLevel = `level${Math.min(i + 1, 7)}`;

    let label = '';
    const isYear = effectiveValueFormat?.isYear || false;
    if (i === 0 && breakRanges.length > 1) {
      // For years use "Before", otherwise "Less than"
      // Use one unit less to clarify boundary (e.g., "Less than $1.29" means values < $1.30)
      label = isYear ? `Before ${formatValue(range.max)}` : `Less than ${formatValue(getOneUnitLess(range.max))}`;
    } else if (i === breakRanges.length - 1 && breakRanges.length > 1) {
      // For years use "or After", otherwise "or more"
      // Use previous break's max for the label to ensure continuity (no visual gap)
      const prevBreakMax = i > 0 ? breakRanges[i - 1].max : range.min;
      label = isYear ? `${formatValue(prevBreakMax)} or After` : `${formatValue(prevBreakMax)} or more`;
    } else {
      // Only show range format if min and max are different
      // Use one unit less for max to clarify boundary (e.g., "$1.30 - $1.49" means values from $1.30 to < $1.50)
      // For the label's min, use the previous break's max to ensure continuity (no visual gap)
      const prevBreakMax = i > 0 ? breakRanges[i - 1].max : range.min;
      label = range.min === range.max
        ? formatValue(range.min)
        : `${formatValue(prevBreakMax)} - ${formatValue(getOneUnitLess(range.max))}`;
    }

    breaks.push({
      minValue: range.min,
      maxValue: range.max,
      label: label,
      symbol: {
        type: "simple-marker",
        style: baseSymbolStyle.style || 'circle',
        color: [...colorArray], // Store RGBA array with new color scheme
        size: baseSymbolStyle.size || 6,
        outline: {
          color: baseSymbolStyle.outline?.color || '#000000',
          width: Number(baseSymbolStyle.outline?.width) || 1
        }
      },
      // Add metadata for new color scheme
      preserveOpacity: true,
      originalOpacity: colorArray[3],
      hasCustomOpacities: true,
      colorSchemeLevel: colorLevel
    });
  }

  console.log("Generated table-driven class breaks with 7-level color scheme:",
    breaks.map((b, i) => `${i}: ${b.colorSchemeLevel}`).join(', '));
  return breaks;
};

// Helper function to generate color ramp
const generateColorRamp = (color1, color2, count) => {
  const validCount = Math.max(1, count);
  if (validCount === 1) return [color1];
  return chroma.scale([color1, color2]).mode('lch').colors(validCount);
};

// Helper function to get project ID from multiple sources
const getProjectId = () => {
  const routeProjectId = new URLSearchParams(window.location.search).get('projectId');
  const localStorageProjectId = localStorage.getItem("currentProjectId");
  const sessionStorageProjectId = sessionStorage.getItem("currentProjectId");
  const projectId = routeProjectId || localStorageProjectId || sessionStorageProjectId;
  // console.log('[NewMapDialog] Project ID sources:', { route: routeProjectId, localStorage: localStorageProjectId, sessionStorage: sessionStorageProjectId, final: projectId });
  return projectId;
};

// Helper function to clean undefined values from object
const cleanObject = (obj) => {
  const cleaned = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined && obj[key] !== null) {
      if (obj[key] === '' || (Array.isArray(obj[key]) && obj[key].length === 0)) return;
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
};

// Helper function to convert area type to string
const convertAreaTypeToString = (value) => {
  // console.log('[NewMapDialog] Converting area type:', { value, type: typeof value });
  if (value === null || value === undefined) {
    // console.log('[NewMapDialog] Area type is null/undefined, defaulting to tract');
    return 'tract';
  }
  if (Array.isArray(value)) {
    // console.warn('[NewMapDialog] Area type received as array:', value);
    return value.length > 0 ? convertAreaTypeToString(value[0]) : 'tract';
  }
  if (typeof value === 'object' && value.value !== undefined) {
    // console.log('[NewMapDialog] Area type is object with value property:', value.value);
    return convertAreaTypeToString(value.value);
  }
  if (typeof value === 'string') {
    // Valid area types matching backend MARKET_AREA_TYPES
    const validAreaTypes = [
      'radius', 'drivetime', 'zip', 'county', 'place', 'tract',
      'block', 'blockgroup', 'cbsa', 'state', 'usa', 'custom', 'site_location'
    ];
    const lowerValue = value.toLowerCase();

    // Handle common frontend variations that need mapping
    if (lowerValue === 'block_group') return 'blockgroup';
    if (lowerValue === 'msa') return 'cbsa';

    if (validAreaTypes.includes(lowerValue)) return lowerValue;
    // console.warn('[NewMapDialog] Unknown area type string:', value, 'defaulting to tract');
    return 'tract';
  }
  if (typeof value === 'number') {
    // console.log('[NewMapDialog] Area type is numeric:', value);
    switch (value) {
      case 11: return 'county';
      case 12: return 'tract';
      case 150: return 'blockgroup';
      default:
        // console.warn('[NewMapDialog] Unknown numeric area type:', value, 'defaulting to tract');
        return 'tract';
    }
  }
  // console.warn('[NewMapDialog] Unexpected area type format:', value, typeof value, 'defaulting to tract');
  return 'tract';
};

// Helper function to validate configuration before sending to API
const validateConfigurationData = (configData) => {
  const errors = [];
  if (!configData.project_id) errors.push('Project ID is required');
  if (!configData.tab_name || configData.tab_name.trim() === '') errors.push('Tab name is required');
  if (!configData.visualization_type) errors.push('Visualization type is required');
  if (!configData.area_type || typeof configData.area_type !== 'string') {
    errors.push(`Area type must be a valid string, received: ${JSON.stringify(configData.area_type)}`);
  }
  if (configData.layer_configuration) {
    if (typeof configData.layer_configuration !== 'string') {
      errors.push('Layer configuration must be a serialized JSON string');
    } else {
      try { JSON.parse(configData.layer_configuration); } 
      catch (e) { errors.push('Layer configuration is not valid JSON'); }
    }
  }
  return errors;
};

/**
 * Generates a unique map name by checking existing configurations in the project.
 * If the name already exists, appends a number suffix (e.g., "Map Name (2)", "Map Name (3)", etc.)
 *
 * @param {string} baseName - The original map name
 * @param {string} projectId - The project ID to check against
 * @returns {Promise<string>} - A unique map name
 */
const generateUniqueMapName = async (baseName, projectId) => {
  if (!projectId) {
    console.warn('[NewMapDialog] No project ID for unique name check, using base name');
    return baseName;
  }

  try {
    // Fetch existing map configurations for this project
    const response = await mapConfigurationsAPI.getAll(projectId);
    const existingConfigs = response.data || [];

    // Extract existing tab names
    const existingNames = existingConfigs.map(config => config.tab_name?.toLowerCase() || '');

    // Check if base name is unique (case-insensitive)
    if (!existingNames.includes(baseName.toLowerCase())) {
      return baseName;
    }

    // Find a unique name by appending a number
    let counter = 2;
    let uniqueName = `${baseName} (${counter})`;

    while (existingNames.includes(uniqueName.toLowerCase())) {
      counter++;
      uniqueName = `${baseName} (${counter})`;
    }

    console.log(`[NewMapDialog] Map name "${baseName}" already exists, using "${uniqueName}"`);
    return uniqueName;
  } catch (error) {
    console.error('[NewMapDialog] Error checking for duplicate names:', error);
    // If we can't check, return the base name and let the backend handle it
    return baseName;
  }
};

// Save configuration to localStorage as fallback
const saveMapConfigurationToLocalStorage = async (mapData) => {
  const MANUAL_SAVE_KEY = "mapConfigurations_default";
  try {
    // console.log("[NewMapDialog] Starting localStorage save");
    let existingConfigs = [];
    try {
      const saved = localStorage.getItem(MANUAL_SAVE_KEY);
      if (saved) existingConfigs = JSON.parse(saved);
    } catch (parseError) {
      console.warn("Could not parse existing localStorage configurations:", parseError);
      existingConfigs = [];
    }
    const configToSave = {
      tab_name: mapData.name || `${mapData.type} Map`,
      visualization_type: mapData.type, // Original map type (e.g. 'heatmap', 'comps')
      area_type: mapData.layerConfiguration?.areaType || // Use areaType from layerConfig if available
                   (mapData.type === 'heatmap' || mapData.type === 'dotdensity' 
                    ? convertAreaTypeToString(mapData.areaType?.value || mapData.areaType || 'tract')
                    : 'custom'), 
      layer_configuration: mapData.layerConfiguration || {},
      order: existingConfigs.length,
      created_at: new Date().toISOString()
    };
    // console.log("[NewMapDialog] LocalStorage config prepared:", { tab_name: configToSave.tab_name, visualization_type: configToSave.visualization_type, area_type: configToSave.area_type });
    const existingIndex = existingConfigs.findIndex(savedConfig =>
      (savedConfig.layer_configuration?.type === mapData.layerConfiguration?.type && savedConfig.layer_configuration?.title === mapData.name) ||
      (savedConfig.visualization_type === mapData.type && savedConfig.tab_name === mapData.name)
    );
    if (existingIndex >= 0) {
      existingConfigs[existingIndex] = { ...existingConfigs[existingIndex], ...configToSave, updated_at: new Date().toISOString() };
      // console.log("[NewMapDialog] Updated localStorage config at index:", existingIndex);
    } else {
      existingConfigs.push(configToSave);
      // console.log("[NewMapDialog] Added new localStorage config");
    }
    localStorage.setItem(MANUAL_SAVE_KEY, JSON.stringify(existingConfigs));
    // console.log("[NewMapDialog] LocalStorage save completed successfully");
    return { success: true, count: existingConfigs.length, type: 'localStorage' };
  } catch (error) {
    console.error("[NewMapDialog] LocalStorage save failed:", error);
    throw new Error(`LocalStorage save failed: ${error.message}`);
  }
};

const saveMapConfigurationToDatabase = async (mapData) => {
  const projectId = getProjectId();
  if (!projectId) {
    throw new Error("No project ID available for saving configuration.");
  }

  console.log("[NewMapDialog] Creating new configuration in database for project:", projectId);

  try {
    // Generate a unique name to avoid duplicate tab_name constraint violation
    const baseName = mapData.name || `${mapData.type} Map`;
    const uniqueName = await generateUniqueMapName(baseName, projectId);

    // Update mapData.name so callers get the actual saved name
    mapData.name = uniqueName;

    const configDataForApi = {
      project_id: projectId,
      project: projectId,
      tab_name: uniqueName,
      visualization_type: mapData.type, // Save the original mapType ('heatmap', 'comps', etc.)
      area_type: mapData.layerConfiguration?.areaType ||
                   (mapData.type === 'heatmap' || mapData.type === 'dotdensity'
                    ? convertAreaTypeToString(mapData.areaType?.value || mapData.areaType || 'tract')
                    : 'custom'),
      layer_configuration: mapData.layerConfiguration || {},
      order: mapData.order || 0
    };

    const cleanedConfigData = cleanObject(configDataForApi);
    if (!cleanedConfigData.project_id && projectId) cleanedConfigData.project_id = projectId;
    if (!cleanedConfigData.project && projectId) cleanedConfigData.project = projectId;

    const apiValidationPayload = {
      ...cleanedConfigData,
      layer_configuration: JSON.stringify(cleanedConfigData.layer_configuration)
    };

    const validationErrors = validateConfigurationData(apiValidationPayload);
    if (validationErrors.length > 0) {
      throw new Error(`Configuration validation failed: ${validationErrors.join(', ')}`);
    }

    console.log("[NewMapDialog] Calling create API with validated data.");
    const createResponse = await mapConfigurationsAPI.create(projectId, cleanedConfigData);

    if (createResponse.data?.id) {
      console.log("[NewMapDialog] Database create successful:", { configId: createResponse.data.id, status: createResponse.status });
      return { success: true, type: 'create', configId: createResponse.data.id, action: 'created' };
    } else {
      throw new Error("Database API returned no configuration ID after creation");
    }
  } catch (apiError) {
    console.error("[NewMapDialog] Database API error during create:", apiError);
    if (apiError.response) {
      console.error("[NewMapDialog] API Error Details:", { status: apiError.response.status, data: apiError.response.data });
      const validationData = apiError.response.data;
      let errorMessage = "Database validation failed: ";
      if (typeof validationData === 'object' && validationData !== null) {
        Object.keys(validationData).forEach(key => {
          errorMessage += `${key}: ${JSON.stringify(validationData[key])}. `;
        });
        throw new Error(errorMessage);
      } else {
        throw new Error(`Database save failed: ${apiError.message}`);
      }
    }
    throw new Error(`Database save failed: ${apiError.message}`);
  }
};

// Primary save function
const saveMapConfiguration = async (mapData) => {
  try {
    const databaseResult = await saveMapConfigurationToDatabase(mapData);
    if (databaseResult.success) {
      try { await saveMapConfigurationToLocalStorage(mapData); /* console.log("[NewMapDialog] Backup localStorage save completed"); */ } 
      catch (localStorageError) { console.warn("[NewMapDialog] Backup localStorage save failed:", localStorageError); }
      return databaseResult;
    }
  } catch (databaseError) {
    console.error("[NewMapDialog] Database save failed, attempting localStorage fallback:", databaseError);
    try {
      const localStorageResult = await saveMapConfigurationToLocalStorage(mapData);
      // console.log("[NewMapDialog] LocalStorage fallback save successful");
      return { success: true, type: 'localStorage', message: 'Saved to local storage (database unavailable)', fallbackReason: databaseError.message };
    } catch (localStorageError) {
      console.error("[NewMapDialog] Both database and localStorage saves failed");
      throw new Error(`Database save failed: ${databaseError.message}. LocalStorage fallback also failed: ${localStorageError.message}`);
    }
  }
  return { success: false, message: "Save operation did not complete."}; // Should not be reached if throws are working
};

/**
 * Generates class breaks specifically for point data colorization
 *
 * @param {Array} data - Array of data objects
 * @param {string} valueColumn - Column name for values
 * @param {number} numClasses - Custom break count (optional)
 * @param {Object} baseSymbolStyle - Base style for symbols (optional)
 * @param {boolean} useTCGColors - Use TCG color palette (optional)
 * @param {boolean} reverseColors - Reverse the color order (optional)
 * @param {Object} valueFormat - Value formatting config with prefix/suffix (optional)
 * @return {Array} - Array of class break objects
 */
const generateClassBreaksForPoints = (data, valueColumn, numClasses = null, baseSymbolStyle = {}, useTCGColors = false, reverseColors = false, valueFormat = null) => {
  const breaks = generateTableDrivenClassBreaks(data, valueColumn, baseSymbolStyle, numClasses, valueFormat);

  // For comp maps, we need to use TCG colors instead of heat map colors
  if (useTCGColors && breaks && breaks.length > 0) {
    // Get colors for the number of breaks we have (Red is first, added sequentially)
    const breakCount = breaks.length;
    const colorsToUse = TCG_COLORS_ARRAY.slice(0, Math.min(breakCount, TCG_COLORS_ARRAY.length));

    breaks.forEach((breakInfo, index) => {
      if (breakInfo.symbol) {
        // REVERSED color assignment: highest values (last break) get Red (index 0)
        // lowest values (first break) get the last color in the sequence
        // For 3 breaks: break 0 (lowest) → Yellow, break 2 (highest) → Red
        const reversedColorIndex = breakCount - 1 - index;
        const colorIndex = reverseColors ? index : reversedColorIndex;
        breakInfo.symbol.color = [...colorsToUse[colorIndex % colorsToUse.length]];
      }
    });
  } else if (!useTCGColors && breaks && breaks.length > 0) {
    // For custom maps, just ensure good opacity
    breaks.forEach((breakInfo) => {
      if (breakInfo.symbol && Array.isArray(breakInfo.symbol.color) && breakInfo.symbol.color.length === 4) {
        // Set opacity to 0.8 for better visibility
        breakInfo.symbol.color[3] = 0.8;
      }
    });
  }

  return breaks;
};

/**
 * Generates class breaks for point size visualization
 * 
 * @param {Array} data - Array of data objects
 * @param {string} valueColumn - Column name for values
 * @param {number} numClasses - Custom break count (optional)
 * @param {number} minSize - Minimum point size (optional)
 * @param {number} maxSize - Maximum point size (optional)
 * @return {Array} - Array of size break objects
 */
const generateSizeBreaksForPoints = (data, valueColumn, numClasses = null, minSize = 10, maxSize = 28, valueFormat = null) => {
  // Validate inputs
  if (!data || data.length === 0 || !valueColumn) {
    console.warn("generateSizeBreaksForPoints: Missing data or valueColumn.");
    return null;
  }

  // Detect columns that should have break values rounded to nearest 10
  const normalizedColumn = valueColumn.replace(/[\s.]/g, '').toLowerCase();
  const shouldRoundToTens = normalizedColumn === 'avgsize' || normalizedColumn === 'avgbaserent' || normalizedColumn === 'avgnetrent';

  // Extract numeric values from data
  // Handles common suffixes like "s" for stories (e.g., "4s" → 4)
  const values = data.map(item => {
    let rawValue = item[valueColumn];
    if (rawValue === null || rawValue === undefined || rawValue === '') return NaN;

    // Convert to string and strip common suffixes (s for stories, ft, etc.)
    if (typeof rawValue === 'string') {
      rawValue = rawValue.trim().replace(/s$/i, '').replace(/ft$/i, '').replace(/stories?$/i, '');
    }

    const num = Number(rawValue);
    return isNaN(num) ? NaN : num;
  }).filter(val => !isNaN(val));

  if (values.length === 0) {
    console.warn(`generateSizeBreaksForPoints: No valid numeric data in '${valueColumn}'.`);
    return null;
  }

  // Determine optimal break count based on number of UNIQUE values
  const uniqueValues = new Set(values);
  const uniqueValueCount = uniqueValues.size;
  const optimalBreakCount = numClasses !== null ?
    numClasses : determineBreakCountByUniqueValues(uniqueValueCount);

  // Generate breaks based on actual data distribution, not predefined ranges
  const breakRanges = calculateDataDrivenBreaks(values, optimalBreakCount, shouldRoundToTens);
  const sizeBreaks = [];

  // Helper function to format a value with prefix/suffix (same as in generateTableDrivenClassBreaks)
  const formatValueWithPrefixSuffix = (value) => {
    const effectiveFormat = valueFormat || {};
    const decimals = effectiveFormat?.decimals !== undefined ? effectiveFormat.decimals : 0;
    const prefix = effectiveFormat?.prefix || '';
    const suffix = effectiveFormat?.suffix || '';
    const multiplier = effectiveFormat?.multiplier || 1;
    const noCommas = effectiveFormat?.noCommas || false;

    const adjustedValue = value * multiplier;
    let formattedNum;
    if (noCommas) {
      formattedNum = decimals > 0 ? adjustedValue.toFixed(decimals) : Math.round(adjustedValue).toString();
    } else {
      formattedNum = decimals > 0
        ? adjustedValue.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        : Math.round(adjustedValue).toLocaleString();
    }
    return `${prefix}${formattedNum}${suffix}`;
  };

  // Create size break objects with appropriate size progression
  for (let i = 0; i < breakRanges.length; i++) {
    const range = breakRanges[i];
    // Calculate proportional size between min and max
    const proportion = breakRanges.length === 1 ? 0.5 : i / (breakRanges.length - 1);
    const currentSize = minSize + (proportion * (maxSize - minSize));

    // Size legend uses single values (the maxValue) since each dot size represents an exact value
    let label = '';
    if (valueFormat && (valueFormat.prefix || valueFormat.suffix)) {
      label = formatValueWithPrefixSuffix(range.max);
    } else {
      label = range.max.toLocaleString();
    }

    sizeBreaks.push({
      minValue: range.min,
      maxValue: range.max,
      label: label,
      size: Math.round(currentSize)
    });
  }

  return sizeBreaks;
};

/**
 * Formats a color value (array or string) into a CSS-compatible color string.
 * 
 * @param {Array|string} color - Color as RGB/RGBA array or string
 * @return {string} - CSS color string (rgb, rgba, or original if already string)
 */
const formatColorForDisplay = (color) => {
  if (Array.isArray(color)) {
    if (color.length === 3) {
      return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    } else if (color.length === 4) {
      return `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`;
    }
  }
  return String(color); // Return as is if already a string or ensure it's a string
};

// Helper to infer value format
const getValueFormatForColumn = (columnName, value) => {
  const name = columnName?.toLowerCase() || '';
  // Special handling for AvgBasePSF with $ and 2 decimal places
  if (name === 'avgbasepsf') return { prefix: '$', decimals: 2, multiplier: 1 };
  if (name.includes('price') || name.includes('value') || name.includes('income') || name.includes('cost') || name.includes('sales')) return { prefix: '$', decimals: 0, multiplier: 1 };
  if (name.includes('percent') || name.includes('rate') || name.includes('pct')) return { suffix: '%', decimals: 1, multiplier: 1 };
  if (name.includes('density')) return { suffix: '/sq mi', decimals: 0, multiplier: 1 };
  if (name.includes('age')) return { suffix: ' yrs', decimals: 1, multiplier: 1 };
  if (name.includes('count') || name.includes('total') || name.includes('number')) return { decimals: 0, multiplier: 1 };
  if (typeof value === 'number') {
    if (!Number.isInteger(value) && value > 0 && value < 1) return { suffix: '%', decimals: 1, multiplier: 100 };
    if (value >= 1 && value <= 100 && (name.includes('%') || name.includes('rate'))) return { suffix: '%', decimals: 1, multiplier: 1 };
    if (value > 1000) return { prefix: '$', decimals: 0, multiplier: 1 };
    return { decimals: Number.isInteger(value) ? 0 : 2, multiplier: 1 };
  }
  return { decimals: 0, prefix: '', suffix: '', multiplier: 1 };
};

// Helper to get specific config from initialLayerConfigurations (from mapConfig.js)
const getInitialConfigForVisualization = (visualizationValue) => {
  if (initialLayerConfigurations[visualizationValue]) {
    return JSON.parse(JSON.stringify(initialLayerConfigurations[visualizationValue]));
  }
  // console.warn(`[NewMapDialog] No specific config found in initialLayerConfigurations for '${visualizationValue}'.`);
  return null;
};

// Generic fallback for dot density attributes
const generateGenericDotDensityAttributes = (field) => {
  return [{ field: field || "value", color: "#8A2BE2", label: field || "Value" }];
};

// Definition of map types for the selection UI
const MAP_TYPE_DEFINITIONS = [
  { id: 'heatmap', name: 'Heat Map', desc: 'Color-coded gradient visualization using enrichment data.', enabled: true },
  { id: 'dotdensity', name: 'Dot Density', desc: 'Points representing data quantity using enrichment data.', enabled: true },
  { id: 'comps', name: 'Comps Map', desc: 'Comparable property data (value based color with preselected AvgBasePSF).', enabled: true },
  { id: 'pipeline', name: 'Pipeline Map', desc: 'Status-based property data.', enabled: true },
  { id: '1varmap', name: '1 Variable Map', desc: 'Single variable visualization (value based color, no preselection).', enabled: true },
  { id: 'custom', name: '2 Variable Map', desc: 'Points with color by Variable 1, size by Variable 2.', enabled: true },
  { id: 'geography', name: 'Map by Geography', desc: 'Upload CSV with areas and values to visualize.', enabled: true },
];

// NewMapDialog Component
const NewMapDialog = ({ isOpen, onClose, onCreateMap, heatVisualizationOptions, dotVisualizationOptions, areaTypes, mapView, projectInfo }) => {
  // For backward compatibility, combine both options for lookups
  const visualizationOptions = [...new Map([...(heatVisualizationOptions || []), ...(dotVisualizationOptions || [])].map(o => [o.value, o])).values()];
  const [step, setStep] = useState(1);
  const [mapName, setMapName] = useState('New Map');
  // Default to the first enabled map type, or 'heatmap' as a fallback
  const [mapType, setMapType] = useState(MAP_TYPE_DEFINITIONS.find(mt => mt.enabled)?.id || 'heatmap');
  const [selectedVisualization, setSelectedVisualization] = useState('');
  const [selectedAreaType, setSelectedAreaType] = useState(areaTypes[0]);
  const [customData, setCustomData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [labelColumn, setLabelColumn] = useState('');
  const [variable1Column, setVariable1Column] = useState('');
  const [variable2Column, setVariable2Column] = useState('');
  const [valueColumn1, setValueColumn1] = useState('');
  const [valueColumn2, setValueColumn2] = useState('');
  const [statusColumn, setStatusColumn] = useState('');
  const [latitudeColumn, setLatitudeColumn] = useState('');
  const [longitudeColumn, setLongitudeColumn] = useState('');
  const [fileError, setFileError] = useState('');
  const [latLongWarning, setLatLongWarning] = useState(''); // Warning for rows missing lat/long values
  const [latLongWarningAcknowledged, setLatLongWarningAcknowledged] = useState(false); // User acknowledged missing lat/long rows will be skipped
  // Geography map specific state
  const [geographyAreaType, setGeographyAreaType] = useState('');
  const [geographyVariable, setGeographyVariable] = useState('');
  const [geographyAreas, setGeographyAreas] = useState([]);
  const [geographyAreaValues, setGeographyAreaValues] = useState({}); // Map of area -> value
  const [geographyCSVFile, setGeographyCSVFile] = useState(null);
  const [geographyTypeAutoDetected, setGeographyTypeAutoDetected] = useState(false);
  const [geographyPastedData, setGeographyPastedData] = useState(''); // Pasted table data for geography maps
  const [showGeographyPasteInput, setShowGeographyPasteInput] = useState(true); // Toggle between file upload and paste (default to paste)
  // New state for map view-based geography heatmaps
  const [geographyDataSource, setGeographyDataSource] = useState(''); // 'view' or 'upload'
  const [geographyVisualizationType, setGeographyVisualizationType] = useState('heatmap'); // 'heatmap' or 'dotdensity' - used for upload data source
  // Multi-variable dot density state
  const [dotDensityVariables, setDotDensityVariables] = useState([]);
  const DOT_DENSITY_COLORS = ['#E60049', '#0BB4FF', '#50E991', '#E6D800', '#9B19F5', '#FFA300', '#DC0AB4', '#00BFA0', '#4682B4', '#8B4513', '#2E8B57', '#FF6347', '#6A5ACD', '#20B2AA', '#CD853F'];
  const [viewAreaCount, setViewAreaCount] = useState(null);
  const [viewCost, setViewCost] = useState(null);
  const [viewExtent, setViewExtent] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [colorClassBreakInfos, setColorClassBreakInfos] = useState(null);
  const [sizeClassBreakInfos, setSizeClassBreakInfos] = useState(null);
  const [numSizeBreaks, setNumSizeBreaks] = useState(null); // null = auto-calculate based on data points
  const [numColorBreaks, setNumColorBreaks] = useState(null); // null = auto-calculate, or 1-10 for manual control (1varmap)
  const [minDotSize, setMinDotSize] = useState(6);
  const [maxDotSize, setMaxDotSize] = useState(24);
  const [variable1Text, setVariable1Text] = useState('');
  const [variable2Text, setVariable2Text] = useState('');
  const [variable1Prefix, setVariable1Prefix] = useState('');
  const [variable2Prefix, setVariable2Prefix] = useState('');
  const [variable1ExplicitNone, setVariable1ExplicitNone] = useState(false); // Track if user explicitly set Variable 1 to None
  const [variable2ExplicitNone, setVariable2ExplicitNone] = useState(false); // Track if user explicitly set Variable 2 to None
  const [colorLegendTitle, setColorLegendTitle] = useState(''); // Custom title for color legend section (2-variable maps)
  const [sizeLegendTitle, setSizeLegendTitle] = useState(''); // Custom title for size legend section (2-variable maps)
  // Pending state for value column changes (1varmap and comps) - requires legend title before applying
  const [pendingValueColumn1, setPendingValueColumn1] = useState(null);
  const [pendingLegendTitle, setPendingLegendTitle] = useState('');
  const [geographyLegendTitle, setGeographyLegendTitle] = useState(''); // Custom title for geography map legend
  const [geographyNumberFormat, setGeographyNumberFormat] = useState('number'); // Number format type: 'number', 'currency', 'percent'
  const [geographyDecimalPlaces, setGeographyDecimalPlaces] = useState(0); // Decimal places for geography map legend values
  const [geographyUseCommas, setGeographyUseCommas] = useState(true); // Whether to use commas in legend numbers
  // Comps/Pipeline number formatting options
  const [compNumberFormat, setCompNumberFormat] = useState('number'); // Number format type: 'number', 'currency', 'percent'
  const [compDecimalPlaces, setCompDecimalPlaces] = useState(0); // Decimal places for comp/pipeline legend values
  const [compUseCommas, setCompUseCommas] = useState(true); // Whether to use commas in comp/pipeline legend numbers
  const [labelOptions, setLabelOptions] = useState({ includeVariables: true, avoidCollisions: true, visibleAtAllZooms: false, fontSize: 10, fontFamily: 'Arial', bold: false, whiteBackground: false, mask: true, showLabelConfig: true });
  const [rendererType, setRendererType] = useState('classBreaks'); // Default to class breaks for comp maps
  const [reverseColors, setReverseColors] = useState(false); // Option to flip/reverse color ramp (false = normal, true = reverse from normal)
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [categorizedVisualizationOptions, setCategorizedVisualizationOptions] = useState({});
  const lastReversedStateRef = useRef(null); // Track last applied reverse state
  const initialLabelAutoConfigDoneRef = useRef(false); // Track if initial label auto-config has been done for 1varmap
  const [pastedTableData, setPastedTableData] = useState(''); // State for pasted table data
  const [showPasteInput, setShowPasteInput] = useState(true); // Toggle between file upload and paste (default to paste)

  // Determine if current map type needs configuration step
  const isHeatmapOrDotDensity = mapType === 'heatmap' || mapType === 'dotdensity';
  const isGeographyMap = mapType === 'geography';

  // Calculate maximum steps based on map type
  // All map types now have 3 steps (Type -> Config -> Review)
  const maxSteps = 3;

  useEffect(() => {
    if (mapType === 'heatmap' || mapType === 'dotdensity') {
      // Use the appropriate options based on map type
      const optionsToUse = mapType === 'heatmap'
        ? (heatVisualizationOptions || [])
        : (dotVisualizationOptions || []);

      const grouped = optionsToUse.reduce((acc, option) => {
        // Use the option's category directly (consolidated categories in mapConfig)
        const categoryName = option.category || 'Other';
        if (!acc[categoryName]) {
          acc[categoryName] = [];
        }
        acc[categoryName].push(option);
        return acc;
      }, {});

      const categoryOrder = [
        'Population & Households', 'Daytime Population', 'Age', 'Age Detail', 'Income',
        'Disposable Income', 'Affluence & Affordability', 'Housing', 'Home Value Ranges',
        'Education', 'Employment & Labor Force', 'Employment by Race', 'Unemployment by Race',
        'Economic Dependency', 'Race & Ethnicity', 'Projected Growth', 'Historical Growth',
        'Future (2029)', 'Density', 'Other'
      ];

      const orderedGrouped = {};
      categoryOrder.forEach(catName => {
          if (grouped[catName]) {
              orderedGrouped[catName] = grouped[catName];
          }
      });
      Object.keys(grouped).forEach(catName => {
          if (!orderedGrouped[catName]) { // Add any categories not in predefined order (e.g. if new ones appear)
              orderedGrouped[catName] = grouped[catName];
          }
      });

      setCategorizedVisualizationOptions(orderedGrouped);
    } else {
      setCategorizedVisualizationOptions({}); // Clear if not heatmap/dotdensity
    }
  }, [heatVisualizationOptions, dotVisualizationOptions, mapType]);


  // Auto-generate color class breaks when data and value column change (for custom/2-variable maps)
  useEffect(() => {
    if (mapType === 'custom' && customData && valueColumn1) {
      // Auto-detect value format for 2-variable maps (same as comps)
      const detectValueFormat = async () => {
        let valueFormat = null;
        if (valueColumn1) {
          valueFormat = getCompFieldFormat(valueColumn1);
          console.log(`[NewMapDialog] Auto-detected format for 2-var color breaks "${valueColumn1}":`, valueFormat);
        }

        const baseSymbol = { style: 'circle', outline: { color: '#000000', width: 0 } };
        // Pass false for reverseColors here - we handle reversing separately
        // Use numColorBreaks if specified, otherwise auto-calculate (null)
        const breaks = generateClassBreaksForPoints(customData, valueColumn1, numColorBreaks, baseSymbol, true, false, valueFormat);
        lastReversedStateRef.current = false; // Reset ref when generating new breaks
        setColorClassBreakInfos(breaks);
      };

      detectValueFormat();
    } else if (mapType !== 'custom' && mapType !== 'comps' && mapType !== '1varmap' && colorClassBreakInfos !== null) { // Clear if not custom, comps, or 1varmap
      setColorClassBreakInfos(null);
    }
  }, [customData, valueColumn1, mapType, numColorBreaks]); // Added numColorBreaks to deps

  // Auto-generate size class breaks when data and size column change (for custom/2-variable maps)
  useEffect(() => {
    if (mapType === 'custom' && customData && valueColumn2) {
      // Auto-detect value format for size variable
      const valueFormat = getCompFieldFormat(valueColumn2);
      console.log(`[NewMapDialog] Auto-detected format for 2-var size breaks "${valueColumn2}":`, valueFormat);
      const breaks = generateSizeBreaksForPoints(customData, valueColumn2, numSizeBreaks, minDotSize, maxDotSize, valueFormat);
      setSizeClassBreakInfos(breaks);
    } else if (mapType !== 'custom' && sizeClassBreakInfos !== null) { // Clear if not custom
      setSizeClassBreakInfos(null);
    }
  }, [customData, valueColumn2, mapType, numSizeBreaks, minDotSize, maxDotSize]); // Added minDotSize and maxDotSize to deps

  // Auto-generate color class breaks for comps and 1varmap maps when using class breaks renderer
  useEffect(() => {
    if ((mapType === 'comps' || mapType === '1varmap') && customData && valueColumn1 && rendererType === 'classBreaks') {
      // Auto-detect value format for comp maps and apply user overrides
      const detectValueFormat = async () => {
        let valueFormat = null;
        if (valueColumn1) {
          const { getCompFieldFormat } = await import('./heatMapGenerator');
          valueFormat = getCompFieldFormat(valueColumn1);
          console.log(`[NewMapDialog] Auto-detected format for class breaks preview "${valueColumn1}":`, valueFormat);
        }

        // Apply user's formatting preferences over auto-detected format
        valueFormat = valueFormat || {};
        valueFormat.decimals = compDecimalPlaces;
        valueFormat.noCommas = !compUseCommas;
        // Apply format type (currency/percent)
        if (compNumberFormat === 'currency') {
          valueFormat.prefix = '$';
          valueFormat.suffix = '';
        } else if (compNumberFormat === 'percent') {
          valueFormat.prefix = '';
          valueFormat.suffix = '%';
        } else {
          // Number format - clear prefix/suffix unless auto-detected had them
          // Only clear if not auto-detected (auto-detect takes precedence for special fields)
          if (compNumberFormat === 'number') {
            valueFormat.prefix = '';
            valueFormat.suffix = '';
          }
        }

        const baseSymbol = { style: 'circle', outline: { color: '#000000', width: 0 } };
        // Pass false for reverseColors here - we handle reversing separately
        // For 1varmap, use numColorBreaks if specified, otherwise auto-calculate (null)
        const numClasses = mapType === '1varmap' ? numColorBreaks : null;
        const breaks = generateClassBreaksForPoints(customData, valueColumn1, numClasses, baseSymbol, true, false, valueFormat);
        lastReversedStateRef.current = false; // Reset ref when generating new breaks
        setColorClassBreakInfos(breaks);
      };

      detectValueFormat();
    } else if ((mapType === 'comps' || mapType === '1varmap') && rendererType === 'simple') {
      // Clear class breaks when using simple renderer
      setColorClassBreakInfos(null);
    } else if (mapType !== 'comps' && mapType !== '1varmap' && mapType !== 'custom' && colorClassBreakInfos !== null) {
        // Already handled by the first useEffect which clears if not custom, comps, or 1varmap
    }
  }, [customData, valueColumn1, mapType, rendererType, numColorBreaks, compNumberFormat, compDecimalPlaces, compUseCommas]); // Added formatting deps

  // Handle reversing colors on existing breaks when reverseColors is toggled or new breaks are generated
  useEffect(() => {
    if (colorClassBreakInfos && colorClassBreakInfos.length > 0) {
      // Check if we need to apply or remove the reverse based on current state
      const needsReverse = reverseColors && lastReversedStateRef.current !== true;
      const needsUnreverse = !reverseColors && lastReversedStateRef.current === true;

      if (needsReverse || needsUnreverse) {
        // Extract current colors from all breaks
        const currentColors = colorClassBreakInfos.map(breakInfo =>
          breakInfo.symbol && breakInfo.symbol.color ? [...breakInfo.symbol.color] : null
        ).filter(color => color !== null);

        if (currentColors.length > 0) {
          // Reverse the color array
          const reversedColors = [...currentColors].reverse();

          // Create new break infos with reversed colors
          const updatedBreaks = colorClassBreakInfos.map((breakInfo, index) => {
            if (breakInfo.symbol && index < reversedColors.length) {
              return {
                ...breakInfo,
                symbol: {
                  ...breakInfo.symbol,
                  color: reversedColors[index]
                }
              };
            }
            return breakInfo;
          });

          setColorClassBreakInfos(updatedBreaks);
          lastReversedStateRef.current = reverseColors; // Update the ref to current state
        }
      }
    } else {
      // Reset the ref when there are no breaks
      lastReversedStateRef.current = null;
    }
  }, [reverseColors, colorClassBreakInfos]); // Trigger when reverseColors or colorClassBreakInfos changes

  // Reset renderer type and auto-select columns when map type changes
  useEffect(() => {
    if (mapType === 'comps' || mapType === '1varmap') {
      setRendererType('classBreaks'); // Default to class breaks for comp and 1varmap maps
    }

    // Auto-select AvgBasePSF for comps maps if available (only on initial load when no value is set)
    if (mapType === 'comps' && customData && columns.length > 0) {
      const avgBasePSFColumn = columns.find(col => col === 'AvgBasePSF' || col.toLowerCase() === 'avgbasepsf');
      if (avgBasePSFColumn && !valueColumn1) {
        console.log(`[NewMapDialog] Auto-selecting AvgBasePSF for comps map: "${avgBasePSFColumn}"`);
        setValueColumn1(avgBasePSFColumn);
      }
    }

    // Auto-select status column for pipeline maps if not already set
    if (mapType === 'pipeline' && customData && columns.length > 0 && !statusColumn) {
      // Use same priority logic as processFileData
      const exactStatusNames = ['status', 'state', 'condition', 'phase', 'stage'];
      const partialStatusKeywords = ['progress', 'pipeline', 'type'];

      // First, look for exact matches (case-insensitive)
      let detectedStatusCol = columns.find(field =>
        exactStatusNames.some(keyword => field.toLowerCase() === keyword)
      );

      // If no exact match, look for fields containing exact status words
      if (!detectedStatusCol) {
        detectedStatusCol = columns.find(field =>
          exactStatusNames.some(keyword => field.toLowerCase().includes(keyword))
        );
      }

      // Finally, fall back to partial keyword matches
      if (!detectedStatusCol) {
        detectedStatusCol = columns.find(field =>
          partialStatusKeywords.some(keyword => field.toLowerCase().includes(keyword))
        );
      }
      if (detectedStatusCol) {
        console.log(`[NewMapDialog] Auto-selecting status column for pipeline map: "${detectedStatusCol}"`);
        setStatusColumn(detectedStatusCol);
      } else {
        // If no status-like column found, try to use the first non-numeric, non-location column
        const nonNumericColumns = columns.filter(col => {
          // Skip location columns
          const locationKeywords = ['lat', 'lon', 'lng', 'latitude', 'longitude', 'x', 'y', 'coord'];
          if (locationKeywords.some(keyword => col.toLowerCase().includes(keyword))) {
            return false;
          }
          // Check if column has non-numeric values
          if (customData && customData.length > 0) {
            const sampleValue = customData[0][col];
            return sampleValue && isNaN(Number(sampleValue));
          }
          return true;
        });

        if (nonNumericColumns.length > 0 && nonNumericColumns[0] !== labelColumn) {
          console.log(`[NewMapDialog] Using first non-numeric column as status: "${nonNumericColumns[0]}"`);
          setStatusColumn(nonNumericColumns[0]);
        }
      }
    }
  }, [mapType, customData, columns, statusColumn, labelColumn, valueColumn1]);

  // Auto-configure pipeline maps with defaults when data is loaded
  useEffect(() => {
    if (mapType === 'pipeline' && customData && columns.length > 0) {
      // 1. Auto-set label column to "Project Name" if available
      if (!labelColumn || labelColumn === columns[0]) {
        const projectNameCol = columns.find(col =>
          col.toLowerCase() === 'project name' ||
          col.toLowerCase() === 'projectname' ||
          col.toLowerCase() === 'name'
        );
        if (projectNameCol) {
          console.log(`[NewMapDialog] Auto-selecting Project Name column for pipeline: "${projectNameCol}"`);
          setLabelColumn(projectNameCol);
        }
      }

      // 2. Auto-set Variable 1 to "Total Units" or similar if available (unless user explicitly set to None)
      if (!variable1Column && !variable1ExplicitNone) {
        const totalUnitsCol = columns.find(col =>
          col.toLowerCase() === 'total units' ||
          col.toLowerCase() === 'totalunits' ||
          col.toLowerCase() === 'units' ||
          col.toLowerCase().includes('total') && col.toLowerCase().includes('unit')
        );
        if (totalUnitsCol) {
          console.log(`[NewMapDialog] Auto-selecting Total Units column for pipeline Variable 1: "${totalUnitsCol}"`);
          setVariable1Column(totalUnitsCol);
        }
      }

      // 3. Auto-set Variable 1 suffix to "Units" (capitalized)
      if (!variable1Text && variable1Column) {
        setVariable1Text('Units');
      }

      // 4. Auto-set map title for pipeline maps
      if (mapName === 'New Map' && projectInfo && variable1Column) {
        const projectName = projectInfo.project_number
          ? `${projectInfo.project_number}${projectInfo.client ? ' - ' + projectInfo.client : ''}`
          : (projectInfo.client || 'Project');
        const varText = variable1Text || 'Units';
        const varCol = variable1Column;
        const generatedTitle = `${projectName} (${varCol} ${varText})`;
        console.log(`[NewMapDialog] Auto-generating pipeline map title: "${generatedTitle}"`);
        setMapName(generatedTitle);
      }
    }
  }, [mapType, customData, columns, labelColumn, variable1Column, variable1Text, mapName, projectInfo, variable1ExplicitNone]);

  // Auto-configure comp maps with default variable columns when data is loaded
  useEffect(() => {
    if (mapType === 'comps' && customData && columns.length > 0) {
      // 1. Auto-set Variable 1 to "Year Built" or similar if available (unless user explicitly set to None)
      if (!variable1Column && !variable1ExplicitNone) {
        const yearBuiltCol = columns.find(col =>
          col.toLowerCase() === 'year built' ||
          col.toLowerCase() === 'yearbuilt' ||
          col.toLowerCase() === 'year_built' ||
          col.toLowerCase() === 'built'
        );
        if (yearBuiltCol) {
          console.log(`[NewMapDialog] Auto-selecting Year Built column for comps Variable 1: "${yearBuiltCol}"`);
          setVariable1Column(yearBuiltCol);
        }
      }

      // 2. Auto-set Variable 2 to "Total Units" or similar if available (unless user explicitly set to None)
      if (!variable2Column && !variable2ExplicitNone) {
        const totalUnitsCol = columns.find(col =>
          col.toLowerCase() === 'total units' ||
          col.toLowerCase() === 'totalunits' ||
          col.toLowerCase() === 'units' ||
          (col.toLowerCase().includes('total') && col.toLowerCase().includes('unit'))
        );
        if (totalUnitsCol) {
          console.log(`[NewMapDialog] Auto-selecting Total Units column for comps Variable 2: "${totalUnitsCol}"`);
          setVariable2Column(totalUnitsCol);
          setVariable2Text('Units');
        }
      }
    }
  }, [mapType, customData, columns, variable1Column, variable2Column, variable1ExplicitNone, variable2ExplicitNone]);

  // Auto-configure 1 variable maps with defaults when data is loaded
  useEffect(() => {
    if (mapType === '1varmap' && customData && columns.length > 0) {
      // 1. Auto-set label column to "none" by default - ONLY on initial load
      // Check if initial auto-config has been done to prevent overriding user selections
      if (!initialLabelAutoConfigDoneRef.current && (!labelColumn || labelColumn === columns[0])) {
        console.log(`[NewMapDialog] Auto-selecting 'none' for 1varmap label column`);
        setLabelColumn('none');
        initialLabelAutoConfigDoneRef.current = true;
      }

      // 2. Auto-set valueColumn1 to Price if available, otherwise leave as placeholder
      if (!valueColumn1) {
        const priceColumn = columns.find(col => col.toLowerCase() === 'price');
        if (priceColumn) {
          console.log(`[NewMapDialog] Auto-selecting Price for 1varmap valueColumn1: "${priceColumn}"`);
          setValueColumn1(priceColumn);
        }
        // If no Price column found, leave valueColumn1 empty to show "Select a value" placeholder
      }
    }
  }, [mapType, customData, columns, labelColumn, valueColumn1]);

  // Reset label auto-config tracking when map type changes
  useEffect(() => {
    initialLabelAutoConfigDoneRef.current = false;
  }, [mapType]);

  // Reset calculating state when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      // When dialog is closed, reset calculating state
      setIsCalculating(false);
    }
  }, [isOpen]);

  // ESC key to close dialog
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        console.log('[NewMapDialog] ESC pressed, closing dialog');
        resetForm();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, isSaving, onClose]);

  // Handle value column selection change for 1varmap and comps - sets pending state requiring legend title
  const handleValueColumn1Select = (selectedColumn) => {
    if (selectedColumn !== valueColumn1) {
      setPendingValueColumn1(selectedColumn);
      // Pre-populate with the formatted default title (mapped name or column name itself)
      setPendingLegendTitle(formatLegendTitle(selectedColumn));
    }
  };

  // Apply the pending value column change with the legend title
  const applyValueColumn1Change = () => {
    if (!pendingLegendTitle.trim()) return;
    setValueColumn1(pendingValueColumn1);
    setColorLegendTitle(pendingLegendTitle.trim());
    setPendingValueColumn1(null);
    setPendingLegendTitle('');
  };

  // Cancel the pending value column change
  const cancelValueColumn1Change = () => {
    setPendingValueColumn1(null);
    setPendingLegendTitle('');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setFileError(''); setLatLongWarning(''); setLatLongWarningAcknowledged(false); setCustomData(null); setColumns([]);
    setLabelColumn(''); setVariable1Column(''); setVariable2Column('');
    setVariable1ExplicitNone(false); setVariable2ExplicitNone(false);
    setValueColumn1(''); setValueColumn2('');
    // Don't clear statusColumn if we're in pipeline mode - it will be auto-detected
    if (mapType !== 'pipeline') {
      setStatusColumn('');
    }
    setLatitudeColumn(''); setLongitudeColumn('');
    setColorClassBreakInfos(null); setSizeClassBreakInfos(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setFileError('File size exceeds 10MB limit'); return; }
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension === 'csv') handleCSVFile(file);
    else if (fileExtension === 'xlsx' || fileExtension === 'xls') handleExcelFile(file);
    else setFileError('Unsupported file format. Please upload a CSV, XLSX, or XLS file.');
  };
  
  const handleCSVFile = (file) => {
    Papa.parse(file, {
      header: true, dynamicTyping: true, skipEmptyLines: 'greedy', transformHeader: header => header.trim(),
      complete: (results) => {
        if (results.errors.length > 0) {
            console.warn("CSV Parsing Errors:", results.errors);
            setFileError(`CSV parsing encountered issues. Check file format near row ${results.errors[0].row}.`);
        }
        if (!results.data || results.data.length === 0) { setFileError("CSV file appears empty or couldn't be parsed correctly."); return; }
        const typedData = results.data.map(row => {
            const newRow = {};
            for (const key in row) {
                const value = row[key];
                if (typeof value === 'string' && value.trim() !== '' && !isNaN(Number(value))) newRow[key] = Number(value);
                else newRow[key] = value; // Keep original type if not clearly a number string
            }
            return newRow;
        });
        processFileData(typedData, results.meta.fields || []);
      },
      error: (error) => { setFileError(`Error parsing CSV file: ${error.message}`); setCustomData(null); setColumns([]); }
    });
  };
  
  const handleExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true, cellNF: false, cellStyles: false });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) throw new Error("Workbook contains no sheets.");
        const worksheet = workbook.Sheets[firstSheetName];
        if (!worksheet) throw new Error(`Sheet '${firstSheetName}' could not be read.`);
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: true, defval: null }); // raw: true tries to preserve types
        if (jsonData.length === 0) { setFileError('Excel sheet appears to have no data rows.'); return; }
        const headers = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
        processFileData(jsonData, headers);
      } catch (error) {
        console.error("Excel Parsing Error:", error);
        setFileError(`Error parsing Excel file: ${error.message}. Ensure the first sheet has data.`);
        setCustomData(null); setColumns([]);
      }
    };
    reader.onerror = (readError) => {
      console.error("File Reading Error:", readError);
      setFileError('Error reading the selected file.');
    };
    reader.readAsArrayBuffer(file);
  };

  const handlePastedData = () => {
    setFileError('');
    setLatLongWarning('');
    setLatLongWarningAcknowledged(false);
    setCustomData(null);
    setColumns([]);
    setLabelColumn('');
    setVariable1Column('');
    setVariable2Column('');
    setVariable1ExplicitNone(false);
    setVariable2ExplicitNone(false);
    setValueColumn1('');
    setValueColumn2('');
    if (mapType !== 'pipeline') {
      setStatusColumn('');
    }
    setLatitudeColumn('');
    setLongitudeColumn('');
    setColorClassBreakInfos(null);
    setSizeClassBreakInfos(null);

    if (!pastedTableData || pastedTableData.trim() === '') {
      setFileError('Please paste table data before parsing.');
      return;
    }

    try {
      // Split into lines and filter out empty lines
      const lines = pastedTableData.split('\n').filter(line => line.trim() !== '');

      if (lines.length < 2) {
        setFileError('Pasted data must contain at least a header row and one data row.');
        return;
      }

      // Parse header row (tab-separated)
      const headers = lines[0].split('\t').map(h => h.trim()).filter(h => h !== '');

      if (headers.length === 0) {
        setFileError('Could not find any column headers in the pasted data.');
        return;
      }

      // Parse data rows
      const parsedData = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split('\t');
        const row = {};

        headers.forEach((header, index) => {
          let value = values[index] !== undefined ? values[index].trim() : '';

          // Type conversion: try to convert to number if possible
          if (value !== '' && value !== null && value !== undefined) {
            // Remove percentage signs and try to parse
            const cleanValue = value.replace(/[%$,]/g, '');
            const numValue = Number(cleanValue);
            if (!isNaN(numValue)) {
              value = numValue;
            }
          }

          row[header] = value === '' ? null : value;
        });

        parsedData.push(row);
      }

      if (parsedData.length === 0) {
        setFileError('No data rows found in pasted table.');
        return;
      }

      // For comps, 1varmap, pipeline, and custom maps, validate that Latitude and Longitude columns exist
      if (mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') {
        const latColumns = ['latitude', 'lat', 'ycoord', 'y'];
        const longColumns = ['longitude', 'long', 'lng', 'xcoord', 'x'];
        const hasLatColumn = headers.some(h => latColumns.includes(h.toLowerCase()) || h.toLowerCase().includes('lat'));
        const hasLongColumn = headers.some(h => longColumns.includes(h.toLowerCase()) || h.toLowerCase().includes('lon') || h.toLowerCase().includes('lng'));

        if (!hasLatColumn || !hasLongColumn) {
          const missing = [];
          if (!hasLatColumn) missing.push('Latitude');
          if (!hasLongColumn) missing.push('Longitude');
          setFileError(`Missing required column(s): ${missing.join(' and ')}. Your data must include Latitude and Longitude columns to plot locations on the map.`);
          return;
        }
      }

      // Check for rows with missing lat/long values before processing
      let hasLatLongIssues = false;
      if (mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') {
        const latColumns = ['latitude', 'lat', 'ycoord', 'y'];
        const longColumns = ['longitude', 'long', 'lng', 'xcoord', 'x'];
        const latCol = headers.find(h => latColumns.includes(h.toLowerCase())) || headers.find(h => h.toLowerCase().includes('lat'));
        const lngCol = headers.find(h => longColumns.includes(h.toLowerCase())) || headers.find(h => h.toLowerCase().includes('lon') || h.toLowerCase().includes('lng'));

        if (latCol && lngCol) {
          for (const row of parsedData) {
            // Skip completely empty rows
            const hasAnyData = Object.values(row).some(val => val !== null && val !== undefined && val !== '');
            if (!hasAnyData) continue;

            const latValue = row[latCol];
            const longValue = row[lngCol];
            const latMissing = latValue === null || latValue === undefined || latValue === '' || (typeof latValue === 'number' && isNaN(latValue));
            const longMissing = longValue === null || longValue === undefined || longValue === '' || (typeof longValue === 'number' && isNaN(longValue));
            if (latMissing || longMissing) {
              hasLatLongIssues = true;
              break;
            }
          }
        }
      }

      // Process the parsed data using existing logic
      processFileData(parsedData, headers);

      // Clear the paste input after successful parsing
      setPastedTableData('');
      setShowPasteInput(false);

      // Only auto-advance if there are no lat/long issues
      if (!hasLatLongIssues) {
        // Automatically advance to next step after successful parsing
        // Give a brief moment for state to update, then proceed
        setTimeout(() => {
          setStep(step + 1);
        }, 100);
      }

    } catch (error) {
      console.error("Table Parsing Error:", error);
      setFileError(`Error parsing pasted table: ${error.message}. Make sure data is tab-separated.`);
      setCustomData(null);
      setColumns([]);
    }
  };
  
  const processFileData = (data, fields) => {
    const validFields = fields.filter(f => f !== undefined && f !== null && String(f).trim() !== '');
    setCustomData(data); setColumns(validFields);
    setColorClassBreakInfos(null); setSizeClassBreakInfos(null);
    setLabelColumn(''); setVariable1Column(''); setVariable2Column('');
    setVariable1ExplicitNone(false); setVariable2ExplicitNone(false);
    setValueColumn1(''); setValueColumn2(''); setStatusColumn('');
    setLatitudeColumn(''); setLongitudeColumn('');
    if (validFields.length > 0) {
      const potentialLabelCols = ['name', 'title', 'label', 'property', 'id', 'address', 'site'];
      const labelCol = validFields.find(f => potentialLabelCols.some(p => f.toLowerCase().includes(p))) || validFields[0];
      setLabelColumn(labelCol);
      const numericColumns = [], potentialLatLon = ['lat', 'lon', 'lng', 'latitude', 'longitude', 'x', 'y', 'coord', 'east', 'north'], potentialIds = ['id', 'objectid', 'fid', 'pk', 'key'];
      validFields.forEach(field => {
        let isNumeric = false, checked = 0, nonNullFound = false;
        for (let i = 0; i < data.length && checked < 5; i++) { // Check up to 5 non-null/undefined values
          const value = data[i]?.[field];
          if (value !== null && value !== undefined && String(value).trim() !== '') { // Added trim check for strings
            nonNullFound = true;
            isNumeric = typeof value === 'number' && !isNaN(value); 
            checked++; 
            if (!isNumeric) break; 
          }
        }
        // Only consider it numeric if at least one non-null value was found and all checked were numeric
        if (nonNullFound && isNumeric && !potentialLatLon.some(p => field.toLowerCase().includes(p)) && !potentialIds.some(p => field.toLowerCase() === p)) {
          numericColumns.push(field);
        }
      });

      // For comp maps, prioritize AvgBasePSF if available
      if (mapType === 'comps' && numericColumns.length > 0) {
        const avgBasePSFColumn = numericColumns.find(col => col === 'AvgBasePSF' || col.toLowerCase() === 'avgbasepsf');
        if (avgBasePSFColumn) {
          console.log(`[processFileData] Auto-selecting AvgBasePSF for comps map: "${avgBasePSFColumn}"`);
          setValueColumn1(avgBasePSFColumn);
        } else {
          setValueColumn1(numericColumns[0]);
        }
      } else if (mapType === '1varmap' && numericColumns.length > 0) {
        // For 1varmap, only auto-select if Price column exists, otherwise leave as placeholder
        const priceColumn = numericColumns.find(col => col.toLowerCase() === 'price');
        if (priceColumn) {
          console.log(`[processFileData] Auto-selecting Price for 1varmap: "${priceColumn}"`);
          setValueColumn1(priceColumn);
        }
        // If no Price column, leave valueColumn1 empty to show "Select a value..." placeholder
      } else if (numericColumns.length > 0) {
        setValueColumn1(numericColumns[0]);
      }

      if (numericColumns.length > 1) setValueColumn2(numericColumns[1]); else if (numericColumns.length > 0) setValueColumn2(numericColumns[0]); // Default to same if only one numeric

      // Pre-fill legend titles for 2-variable (custom) maps when variables are auto-selected
      if (mapType === 'custom') {
        const autoCol1 = numericColumns[0];
        const autoCol2 = numericColumns.length > 1 ? numericColumns[1] : numericColumns[0];
        if (autoCol1) setColorLegendTitle(`Color: ${formatLegendTitle(autoCol1)}`);
        if (autoCol2) setSizeLegendTitle(`Size: ${formatLegendTitle(autoCol2)}`);
      }

      // Enhanced status column detection - especially important for pipeline maps
      // Priority order: exact matches first, then partial matches
      const exactStatusNames = ['status', 'state', 'condition', 'phase', 'stage'];
      const partialStatusKeywords = ['progress', 'pipeline', 'type'];

      // First, look for exact matches (case-insensitive)
      let statusCol = validFields.find(field =>
        exactStatusNames.some(keyword => field.toLowerCase() === keyword)
      );

      // If no exact match, look for fields containing exact status words
      if (!statusCol) {
        statusCol = validFields.find(field =>
          exactStatusNames.some(keyword => field.toLowerCase().includes(keyword))
        );
      }

      // Finally, fall back to partial keyword matches
      if (!statusCol) {
        statusCol = validFields.find(field =>
          partialStatusKeywords.some(keyword => field.toLowerCase().includes(keyword))
        );
      }

      // If no status column found by keywords and we're in pipeline mode,
      // try to find a non-numeric column that could be a status
      if (!statusCol && mapType === 'pipeline') {
        const nonNumericNonLocationColumns = validFields.filter(field => {
          // Skip location and label columns
          if (potentialLatLon.some(p => field.toLowerCase().includes(p))) return false;
          if (field === labelCol) return false;

          // Check if column contains non-numeric data
          for (let i = 0; i < Math.min(5, data.length); i++) {
            const value = data[i]?.[field];
            if (value && isNaN(Number(value))) {
              return true; // Found non-numeric value, could be status
            }
          }
          return false;
        });

        if (nonNumericNonLocationColumns.length > 0) {
          statusCol = nonNumericNonLocationColumns[0];
          console.log(`[processFileData] Auto-detected potential status column for pipeline: "${statusCol}"`);
        }
      }

      if (statusCol) {
        setStatusColumn(statusCol);
        console.log(`[processFileData] Status column set to: "${statusCol}" (available columns: ${validFields.join(', ')})`);
        // Add a small delay to ensure UI updates
        setTimeout(() => {
          console.log(`[processFileData] Status column state after delay: "${statusColumn}"`);
        }, 100);
      } else {
        console.log(`[processFileData] No status column detected from available columns: ${validFields.join(', ')}`);
      }
      
      const latColumns = ['latitude', 'lat', 'ycoord', 'y'];
      const longColumns = ['longitude', 'long', 'lng', 'xcoord', 'x'];
      let latCol = validFields.find(f => latColumns.includes(f.toLowerCase())) || validFields.find(f => f.toLowerCase().includes('lat'));
      let lngCol = validFields.find(f => longColumns.includes(f.toLowerCase())) || validFields.find(f => f.toLowerCase().includes('lon') || f.toLowerCase().includes('lng'));
      if (latCol) setLatitudeColumn(latCol); if (lngCol) setLongitudeColumn(lngCol);

      // Check for rows with missing lat/long values (for map types that need coordinates)
      if ((mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && latCol && lngCol) {
        const rowsWithMissingLatLong = [];
        data.forEach((row, index) => {
          // Skip completely empty rows (trailing rows from Excel)
          const hasAnyData = Object.values(row).some(val => val !== null && val !== undefined && val !== '');
          if (!hasAnyData) return;

          const latValue = row[latCol];
          const longValue = row[lngCol];
          const latMissing = latValue === null || latValue === undefined || latValue === '' || (typeof latValue === 'number' && isNaN(latValue));
          const longMissing = longValue === null || longValue === undefined || longValue === '' || (typeof longValue === 'number' && isNaN(longValue));
          if (latMissing || longMissing) {
            rowsWithMissingLatLong.push(index + 2); // +2 because: +1 for header row, +1 for 1-based index
          }
        });

        if (rowsWithMissingLatLong.length > 0) {
          const rowCount = rowsWithMissingLatLong.length;
          const rowList = rowsWithMissingLatLong.length <= 5
            ? rowsWithMissingLatLong.join(', ')
            : `${rowsWithMissingLatLong.slice(0, 5).join(', ')}...`;
          setLatLongWarning(`${rowCount} row${rowCount > 1 ? 's' : ''} (row${rowCount > 1 ? 's' : ''} ${rowList}) ${rowCount > 1 ? 'are' : 'is'} missing Latitude or Longitude values. These rows will be skipped.`);
        } else {
          setLatLongWarning('');
        }
      }
    }
  };

  const getSampleValue = (col) => {
    if (!customData || !col) return undefined;
    for (const row of customData) if (row && row[col] !== null && row[col] !== undefined) return row[col];
    return undefined;
  };

  const generateFormattedTitle = () => {
    const parts = [];
    if (labelColumn && labelColumn !== 'none') parts.push(labelColumn || "Label");
    else if (valueColumn1 || valueColumn2 || selectedVisualization) parts.push("Data Visualization");
    else parts.push("Map");
    const variableParts = [];
    if (variable1Column) variableParts.push((variable1Prefix || '') + variable1Column + (variable1Text || ''));
    if (variable2Column) variableParts.push((variable2Prefix || '') + variable2Column + (variable2Text || ''));
    if (variableParts.length > 0) parts.push('(' + variableParts.join(", ") + ')');
    return parts.join(" ");
  };

  const generateSampleLabel = () => {
    if (!customData || customData.length === 0) return null;
    const parts = [];
    if (labelColumn && labelColumn !== 'none') {
      const sampleVal = getSampleValue(labelColumn);
      parts.push(sampleVal !== undefined ? String(sampleVal) : '—');
    } else if (valueColumn1 || valueColumn2 || selectedVisualization) {
      parts.push("Data Visualization");
    } else {
      return null; // No meaningful sample to show
    }
    const variableParts = [];
    if (variable1Column) {
      const sampleVal = getSampleValue(variable1Column);
      variableParts.push((variable1Prefix || '') + (sampleVal !== undefined ? String(sampleVal) : '—') + (variable1Text || ''));
    }
    if (variable2Column) {
      const sampleVal = getSampleValue(variable2Column);
      variableParts.push((variable2Prefix || '') + (sampleVal !== undefined ? String(sampleVal) : '—') + (variable2Text || ''));
    }
    if (variableParts.length > 0) parts.push('(' + variableParts.join(", ") + ')');
    return parts.join(" ");
  };

  const resetForm = () => {
    setStep(1); setMapName('New Map');
    setMapType(MAP_TYPE_DEFINITIONS.find(mt => mt.enabled)?.id || 'heatmap'); // Reset to default enabled map type
    setSelectedVisualization('');
    setSelectedAreaType(areaTypes[0]); setCustomData(null); setColumns([]);
    setLabelColumn(''); setVariable1Column(''); setVariable2Column('');
    setVariable1ExplicitNone(false); setVariable2ExplicitNone(false);
    setVariable1Text(''); setVariable2Text(''); setVariable1Prefix(''); setVariable2Prefix(''); setValueColumn1(''); setValueColumn2('');
    setColorLegendTitle(''); setSizeLegendTitle(''); // Reset legend section titles
    setStatusColumn(''); setLatitudeColumn(''); setLongitudeColumn(''); setFileError(''); setLatLongWarning(''); setLatLongWarningAcknowledged(false);
    setColorClassBreakInfos(null); setSizeClassBreakInfos(null); setNumSizeBreaks(null); setNumColorBreaks(null);
    setLabelOptions({ includeVariables: true, avoidCollisions: true, visibleAtAllZooms: false, fontSize: 10, fontFamily: 'Arial', bold: false, whiteBackground: false, showLabelConfig: false });
    setRendererType('classBreaks'); // Reset to default renderer type
    setReverseColors(false); // Reset color flip option to default (unchecked)
    lastReversedStateRef.current = null; // Reset reverse tracking ref
    initialLabelAutoConfigDoneRef.current = false; // Reset label auto-config tracking ref
    // Reset geography-specific state
    setGeographyAreas([]); setGeographyAreaValues({}); setGeographyCSVFile(null);
    setGeographyAreaType(''); setGeographyVariable(''); setGeographyTypeAutoDetected(false);
    setGeographyPastedData(''); setShowGeographyPasteInput(true);
    // Reset map view-based geography state
    setGeographyDataSource('');
    setViewAreaCount(null);
    setViewCost(null);
    setViewExtent(null);
    setIsCalculating(false); // CRITICAL: Reset calculating state to remove overlay
    setIsSaving(false); if (fileInputRef.current) fileInputRef.current.value = '';
    // Reset paste table state
    setPastedTableData('');
    setShowPasteInput(true);
  };

  const handleCreateMap = async () => {
    console.log("[NewMapDialog] Starting map creation process...");
    
    try {
      // Set saving state
      setIsSaving(true);
      
      // Validate required fields based on map type
      if ((mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && !customData) {
        setFileError('Please upload a data file.');
        setIsSaving(false);
        return;
      }

      // Validate heatmap/dotdensity - now uses enrichment-based approach
      if (mapType === 'heatmap' || mapType === 'dotdensity') {
        // For heatmap, check single variable; for dotdensity, check multi-variables
        if (mapType === 'heatmap') {
          if (!geographyVariable) {
            setFileError('Please select an enrichment variable.');
            setIsSaving(false);
            return;
          }
        } else {
          // Dot density - check for at least one variable with a field selected
          if (dotDensityVariables.length === 0) {
            setFileError('Please add at least one enrichment variable.');
            setIsSaving(false);
            return;
          }
          if (!dotDensityVariables.every(v => v.field)) {
            setFileError('Please select a variable for each added row.');
            setIsSaving(false);
            return;
          }
        }
        if (!geographyAreaType) {
          setFileError('Please select an area type.');
          setIsSaving(false);
          return;
        }
        if (geographyDataSource === 'view') {
          if (!viewExtent) {
            setFileError('Please calculate the area count first.');
            setIsSaving(false);
            return;
          }
          if (viewAreaCount && viewAreaCount > 2000) {
            setFileError(`Too many areas (${viewAreaCount.toLocaleString()}). Maximum is 2,000 areas. Please zoom in or select a smaller region.`);
            setIsSaving(false);
            return;
          }
        }
      }

      // Additional validation for custom data maps
      if (mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') {
        if (!labelColumn && labelColumn !== 'none') {
          setFileError('Please select a Label column or "None".');
          setIsSaving(false);
          return;
        }
        if (!latitudeColumn || !longitudeColumn) {
          setFileError('Please select Latitude and Longitude columns.');
          setIsSaving(false);
          return;
        }
        if (mapType === 'pipeline' && !statusColumn) {
          setFileError('Please select a Status column for pipeline visualization.');
          setIsSaving(false);
          return;
        }
      }

      // Build map configuration based on map type
      let mapConfiguration = {};

      if (mapType === 'geography') {
        console.log("[NewMapDialog] Creating value-based geography map");

        // Validate geography map data
        if (!geographyCSVFile || geographyAreas.length === 0) {
          setFileError('Please upload a CSV file with geographic areas and values.');
          setIsSaving(false);
          return;
        }
        if (Object.keys(geographyAreaValues).length === 0) {
          setFileError('Please ensure your CSV has a "Value" column with numeric values.');
          setIsSaving(false);
          return;
        }
        if (!geographyLegendTitle.trim()) {
          setFileError('Please enter a legend title.');
          setIsSaving(false);
          return;
        }

        // Create a value-based geography map configuration
        console.log("[NewMapDialog] Creating geography map for", geographyAreas.length, "areas with values");

        mapConfiguration = {
          type: 'geography-value',
          field: 'Value',
          title: geographyLegendTitle,
          numberFormat: geographyNumberFormat,
          decimalPlaces: geographyDecimalPlaces,
          useCommas: geographyUseCommas,
          areaType: geographyAreaType,
          geographyList: geographyAreas,
          geographyValues: geographyAreaValues,
          specificAreasOnly: true,
          renderMode: 'value-geography',
          colorScheme: 'heatmap',
          customColors: GEOGRAPHY_COLOR_SCHEME.colors,
          geographyMapConfig: {
            areas: geographyAreas,
            values: geographyAreaValues,
            areaType: geographyAreaType,
            colorScheme: 'heatmap',
            legendTitle: geographyLegendTitle,
            numberFormat: geographyNumberFormat,
            decimalPlaces: geographyDecimalPlaces,
            useCommas: geographyUseCommas
          }
        };

        console.log("[NewMapDialog] Geography map will render areas with values:",
          geographyAreas.slice(0, 3).map(a => `${a}: ${geographyAreaValues[a]}`).join(', '),
          geographyAreas.length > 3 ? `... and ${geographyAreas.length - 3} more` : '');

      } else if (mapType === 'heatmap' || mapType === 'dotdensity') {
        // New enrichment-based heatmap/dotdensity handling
        console.log(`[NewMapDialog] Creating enrichment-based ${mapType} map`);

        // Get project ID using the helper function
        const projectId = getProjectId();
        if (!projectId) {
          setFileError('Project ID not found. Please ensure you are in a project context.');
          setIsSaving(false);
          return;
        }

        // Look up the label for the selected variable (for heatmap)
        const selectedVarOption = visualizationOptions.find(opt => opt.value === geographyVariable);
        const variableLabel = selectedVarOption?.label || geographyVariable;

        // For dot density, build attributes array from multi-variable selection
        const dotDensityAttributes = mapType === 'dotdensity' ? dotDensityVariables.map(v => ({
          field: v.field,
          color: v.color,
          label: v.label || v.field
        })) : null;

        // For dot density, use first variable as the primary field (for compatibility)
        const primaryField = mapType === 'dotdensity' && dotDensityVariables.length > 0
          ? dotDensityVariables[0].field
          : geographyVariable;
        const primaryLabel = mapType === 'dotdensity' && dotDensityVariables.length > 0
          ? dotDensityVariables[0].label
          : variableLabel;

        // Handle view-based or upload-based data source
        if (geographyDataSource === 'view') {
          console.log(`[NewMapDialog] Creating view-based ${mapType} map`);

          // Create a geography map configuration with extent-based filtering
          mapConfiguration = {
            type: mapType === 'dotdensity' ? 'geography-dotdensity' : 'geography-heatmap',
            visualizationKey: primaryField,
            field: primaryField,
            fieldLabel: primaryLabel,
            title: primaryLabel,
            areaType: geographyAreaType,
            specificAreasOnly: true,
            renderMode: mapType === 'heatmap' ? 'extent-based-geography' : 'extent-based-geography-dotdensity',
            viewExtent: viewExtent,
            areaCount: viewAreaCount,
            estimatedCost: mapType === 'dotdensity' ? viewCost * dotDensityVariables.length : viewCost,
            visualizationType: mapType, // 'heatmap' or 'dotdensity'
            needsLegendConfig: true,
            // Multi-variable dot density attributes
            ...(mapType === 'dotdensity' && {
              attributes: dotDensityAttributes,
              isDotDensity: true
            }),
            geographyMapConfig: {
              dataSource: 'view',
              areaType: geographyAreaType,
              variable: primaryField,
              variableLabel: primaryLabel,
              projectId: projectId,
              extent: viewExtent,
              areaCount: viewAreaCount,
              visualizationType: mapType,
              ...(mapType === 'dotdensity' && { attributes: dotDensityAttributes })
            }
          };

          console.log(`[NewMapDialog] View-based ${mapType} map will process`, viewAreaCount, geographyAreaType, "areas within extent");
          if (mapType === 'dotdensity') {
            console.log(`[NewMapDialog] Dot density with ${dotDensityVariables.length} variables:`, dotDensityAttributes);
          }
        }

      } else {
        // Custom data maps configuration
        // Set legend title based on map type (uses formatLegendTitle from module scope)
        // Use colorLegendTitle if provided, otherwise fall back to default
        let legendTitle = '';
        if (mapType === 'pipeline') {
          legendTitle = colorLegendTitle || 'Project Status';
        } else if (mapType === 'comps') {
          legendTitle = colorLegendTitle || formatLegendTitle(valueColumn1);
        } else if (mapType === '1varmap') {
          legendTitle = colorLegendTitle || formatLegendTitle(valueColumn1); // Use selected variable as title (same as comps)
        } else if (mapType === 'custom') {
          legendTitle = ''; // No title needed - dual legends make it clear
        }

        // Auto-detect value format for comp maps based on the value column, then apply user overrides
        let valueFormat = undefined;
        if ((mapType === 'comps' || mapType === '1varmap') && valueColumn1) {
          const { getCompFieldFormat } = await import('./heatMapGenerator');
          valueFormat = getCompFieldFormat(valueColumn1);
          console.log(`[NewMapDialog] Auto-detected legend format for "${valueColumn1}":`, valueFormat);

          // Apply user's formatting preferences over auto-detected format
          valueFormat = valueFormat || {};
          valueFormat.decimals = compDecimalPlaces;
          valueFormat.noCommas = !compUseCommas;
          // Apply format type (currency/percent)
          if (compNumberFormat === 'currency') {
            valueFormat.prefix = '$';
            valueFormat.suffix = '';
          } else if (compNumberFormat === 'percent') {
            valueFormat.prefix = '';
            valueFormat.suffix = '%';
          } else if (compNumberFormat === 'number') {
            valueFormat.prefix = '';
            valueFormat.suffix = '';
          }
          console.log(`[NewMapDialog] Final legend format with user overrides:`, valueFormat);
        }

        mapConfiguration = {
          type: mapType,
          title: legendTitle,
          customData: {
            data: customData,
            nameColumn: labelColumn === 'none' ? null : labelColumn
          },
          labelColumn: labelColumn === 'none' ? null : labelColumn,
          hasNoLabels: labelColumn === 'none',
          hideAllLabels: labelColumn === 'none',
          variable1Column: variable1Column,
          variable2Column: variable2Column,
          variable1Text: variable1Text,
          variable2Text: variable2Text,
          variable1Prefix: variable1Prefix,
          variable2Prefix: variable2Prefix,
          valueColumn: valueColumn1,
          valueColumn1: valueColumn1,
          valueColumn2: valueColumn2,
          // For comps and 1varmap, set field to the actual value column for the renderer
          field: (mapType === 'comps' || mapType === '1varmap') ? valueColumn1 : undefined,
          statusColumn: statusColumn,
          latitudeColumn: latitudeColumn,
          longitudeColumn: longitudeColumn,
          labelOptions: labelOptions,
          titleFormat: generateFormattedTitle(),
          // For comp and 1varmap maps, use classBreakInfos; for custom maps, use colorClassBreakInfos
          classBreakInfos: (mapType === 'comps' || mapType === '1varmap') && rendererType === 'classBreaks' ? colorClassBreakInfos : undefined,
          colorClassBreakInfos: mapType === 'custom' ? colorClassBreakInfos : undefined,
          sizeClassBreakInfos: sizeClassBreakInfos,
          numSizeBreaks: numSizeBreaks,
          rendererType: (mapType === 'comps' || mapType === '1varmap') ? rendererType : (mapType === 'custom' && valueColumn1 ? 'classBreaks' : 'simple'),
          reverseColors: reverseColors, // Add reverseColors to config
          valueFormat: valueFormat, // Auto-detected legend prefix/suffix formatting
          colorLegendTitle: (mapType === 'custom' || mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline') ? colorLegendTitle : undefined, // Custom title for legend section
          sizeLegendTitle: mapType === 'custom' ? sizeLegendTitle : undefined, // Custom title for size legend section
          symbol: {
            style: 'circle',
            size: 6,
            minSize: minDotSize,
            maxSize: maxDotSize,
            color: (mapType === 'comps' || mapType === '1varmap') ? '#800080' : '#FF0000',
            outline: {
              color: '#000000',
              width: 1
            }
          }
        };

        // Add type-specific configurations
        if (mapType === 'pipeline') {
          mapConfiguration.statusColumn = statusColumn;

          // All possible status colors
          const allStatusColors = {
            "Under Construction": "#BF0000",
            "Approved": "#FF7A0D",
            "Pending": "#F8F200",
            "Proposed": "#00BF2C",
            "Conceptual": "#009B9B",
            "Stalled": "#003380",
            "Inactive": "#5C00B8",
            "Pre-Leasing/Pre-Selling": "#D6009E",
            "Complete": "#AEAAAA",
          };

          // Get unique statuses from the actual data and filter statusColors
          const filteredStatusColors = { default: "#FFA500" };
          if (customData && statusColumn) {
            const uniqueStatuses = new Set(
              customData.map(row => String(row[statusColumn] || '').trim()).filter(Boolean)
            );
            uniqueStatuses.forEach(status => {
              filteredStatusColors[status] = allStatusColors[status] || "#FFA500";
            });
          }

          mapConfiguration.statusColors = filteredStatusColors;
          mapConfiguration.type = 'pipe';

          console.log("[NewMapDialog] Pipeline configuration created:", {
            title: mapConfiguration.title,
            statusColumn: statusColumn,
            hasCustomData: !!customData,
            dataRows: customData?.length || 0,
            uniqueStatuses: Object.keys(filteredStatusColors).filter(k => k !== 'default'),
            sampleStatuses: customData?.length > 0 ? customData.slice(0, 3).map(row => row[statusColumn]) : []
          });
        }
      }

      // Create the map data object to pass to parent
      // For geography maps, convert the area type string to the proper areaType object
      let finalAreaType = selectedAreaType;
      if (mapType === 'geography' || mapType === 'heatmap' || mapType === 'dotdensity') {
        const areaTypeMapping = {
          'zip': 'Zip Code',
          'county': 'County',
          'place': 'Place',
          'tract': 'Census Tract',
          'blockgroup': 'Block Group',
          'cbsa': 'CBSA',
          'state': 'State'
        };
        const areaTypeLabel = areaTypeMapping[geographyAreaType] || 'Zip Code';
        finalAreaType = areaTypes.find(at => at.label === areaTypeLabel) || areaTypes[0];
        console.log(`[NewMapDialog] Mapped geography type '${geographyAreaType}' to areaType:`, finalAreaType);
      }

      const mapData = {
        name: mapName.trim() || generateFormattedTitle(),
        type: mapConfiguration.type || mapType,  // Use configuration type for geography maps
        visualizationType: selectedVisualization || mapType,
        areaType: finalAreaType,
        layerConfiguration: mapConfiguration,
        originalFormData: {
          mapType,
          selectedVisualization,
          selectedAreaType,
          customData,
          labelColumn,
          variable1Column,
          variable2Column,
          variable1Text,
          variable2Text,
          variable1Prefix,
          variable2Prefix,
          valueColumn1,
          valueColumn2,
          numSizeBreaks,
          statusColumn,
          latitudeColumn,
          longitudeColumn,
          labelOptions,
          rendererType
        }
      };

      console.log("[NewMapDialog] Saving map configuration to database...");

      // Save the map configuration to database/storage
      try {
        const saveResult = await saveMapConfiguration(mapData);
        console.log("[NewMapDialog] Map configuration saved:", saveResult);
        
        if (saveResult.success && saveResult.configId) {
          mapData.configId = saveResult.configId;
          mapData.mapConfigId = saveResult.configId;
          mapData.alreadySaved = true;
        }
      } catch (saveError) {
        console.error("[NewMapDialog] Error saving map configuration:", saveError);
      }

      console.log("[NewMapDialog] Calling onCreateMap with data:", {
        name: mapData.name,
        type: mapData.type,
        visualizationType: mapData.visualizationType,
        hasCustomData: !!customData,
        configType: mapConfiguration.type,
        configId: mapData.configId,
        alreadySaved: mapData.alreadySaved,
        isDataDriven: mapConfiguration.dataOptimized || false
      });

      // Call the parent's map creation handler
      if (onCreateMap && typeof onCreateMap === 'function') {
        await onCreateMap(mapData);
        console.log("[NewMapDialog] Map creation completed successfully");

        // Reset form and close dialog
        resetForm();
        onClose();
      } else {
        throw new Error("onCreateMap function not provided or invalid");
      }

    } catch (error) {
      console.error("[NewMapDialog] Error during map creation:", error);
      setFileError(`Failed to create map: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const onCancel = () => {
    resetForm();
    onClose();
  };

  /**
   * Enhanced nextStep function that intelligently handles step navigation
   * based on map type. For heatmap/dotdensity maps, skips configuration
   * step and goes directly from step 1 to step 3 (review).
   */
  const nextStep = () => {
    if (step === 1) {
      // If on paste input mode and data hasn't been parsed yet, parse it first
      if ((mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && showPasteInput && !customData && pastedTableData.trim()) {
        handlePastedData();
        return; // handlePastedData will handle errors and will not proceed if parsing fails
      }

      // Validate step 1 requirements for all map types
      if ((mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && !customData) {
        if (showPasteInput) {
          setFileError('Please paste table data before proceeding.');
        } else {
          setFileError('Please upload a data file.');
        }
        return;
      }
      // Block progression if there are rows missing lat/long values (unless user acknowledged)
      if ((mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && latLongWarning && !latLongWarningAcknowledged) {
        return; // Warning message displayed, user must acknowledge to continue
      }
      // Geography map requires area type selection before proceeding
      if (mapType === 'geography' && !geographyAreaType) {
        setFileError('Please select an area type before proceeding.');
        return;
      }
    }
    
    if (step === 2) { // This is the configuration step
      // Heatmap/Dotdensity validation
      if (mapType === 'heatmap' || mapType === 'dotdensity') {
        // For heatmap, check single variable; for dotdensity, check multi-variables
        if (mapType === 'heatmap') {
          if (!geographyVariable) {
            setFileError('Please select an enrichment variable.');
            return;
          }
        } else {
          // Dot density - check for at least one variable with a field selected
          if (dotDensityVariables.length === 0) {
            setFileError('Please add at least one enrichment variable.');
            return;
          }
          if (!dotDensityVariables.every(v => v.field)) {
            setFileError('Please select a variable for each added row.');
            return;
          }
        }
        if (!geographyAreaType) {
          setFileError('Please select an area type.');
          return;
        }
        // View-based validation (only option for heatmap/dotdensity)
        if (viewAreaCount === null || viewCost === null) {
          setFileError('Please click "Calculate" to determine area count and cost before proceeding.');
          return;
        }
        if (viewAreaCount > 2000) {
          setFileError(`Too many areas (${viewAreaCount.toLocaleString()}). Maximum is 2,000 areas. Please zoom in or select a smaller region.`);
          return;
        }
      }
      // Geography map validation
      if (mapType === 'geography') {
        if (!geographyCSVFile || geographyAreas.length === 0) {
          setFileError('Please upload a CSV file with geographic areas and values.');
          return;
        }
        if (Object.keys(geographyAreaValues).length === 0) {
          setFileError('Please ensure your CSV has a "Value" column with numeric values.');
          return;
        }
      }
      // Configuration step validation for custom data maps only
      if (mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') {
          if (!labelColumn && labelColumn !== 'none') {
            setFileError('Please select a Label column or "None".');
            return;
          }
          if (!latitudeColumn || !longitudeColumn) {
            setFileError('Please select Latitude and Longitude columns.');
            return;
          }
          if (mapType === 'pipeline' && !statusColumn) {
            setFileError('Please select a Status column for pipeline visualization.');
            return;
          }
          if (customData && customData.length > 0) {
              // Helper to strip common suffixes (s for stories, ft, etc.) before numeric check
              const stripSuffixForNumericCheck = (val) => {
                if (val === null || val === undefined) return val;
                const str = String(val).trim();
                return str.replace(/s$/i, '').replace(/ft$/i, '').replace(/stories?$/i, '');
              };

              let latValid = false, lonValid = false, val2Numeric = true, checked = 0;
              for(let i = 0; i < customData.length && checked < 5; i++) {
                  const row = customData[i];
                  if (row) {
                      if (latitudeColumn && typeof row[latitudeColumn] === 'number' && !isNaN(Number(row[latitudeColumn]))) latValid = true;
                      if (longitudeColumn && typeof row[longitudeColumn] === 'number' && !isNaN(Number(row[longitudeColumn]))) lonValid = true;

                      // Strip suffixes like "s" (stories) before checking if numeric (only for valueColumn2/size)
                      const val2Cleaned = stripSuffixForNumericCheck(row[valueColumn2]);

                      // Note: valueColumn1 (color) now supports categorical data, so we only validate valueColumn2 (size)
                      if (mapType === 'custom' && valueColumn2 && row[valueColumn2] !== null && row[valueColumn2] !== undefined && String(row[valueColumn2]).trim() !== '' && isNaN(Number(val2Cleaned))) val2Numeric = false;
                      
                      // Only increment checked if relevant columns are being validated for this row
                      if ((latitudeColumn && row[latitudeColumn] !== undefined) || 
                          (longitudeColumn && row[longitudeColumn] !== undefined) ||
                          (valueColumn1 && row[valueColumn1] !== undefined) ||
                          (valueColumn2 && row[valueColumn2] !== undefined)) {
                        checked++;
                      }
                  }
              }
              // Check only if a column is selected
              if (latitudeColumn && !latValid) { 
                setFileError(`Latitude column ('${latitudeColumn}') has non-numeric or missing data in first 5 non-empty rows.`); 
                return; 
              }
              if (longitudeColumn && !lonValid) { 
                setFileError(`Longitude column ('${longitudeColumn}') has non-numeric or missing data in first 5 non-empty rows.`); 
                return; 
              }
              // Note: We now support categorical (non-numeric) data for valueColumn1 color classification
              // so we no longer block non-numeric data here
              if (mapType === 'custom' && valueColumn2 && !val2Numeric) { 
                setFileError(`Value Column 2 (Size) ('${valueColumn2}') has non-numeric data. Size breaks cannot be generated.`); 
                setSizeClassBreakInfos(null); 
                return; 
              }
          }
      }
    }
    
    // Auto-set geographyDataSource to 'view' for heatmap/dotdensity when entering step 2
    if (step === 1 && (mapType === 'heatmap' || mapType === 'dotdensity')) {
      setGeographyDataSource('view');
    }

    // Normal step progression
    setStep(step + 1);
    setFileError('');
  };

  /**
   * prevStep function that handles backward navigation.
   * All map types now use 3-step flow.
   * When going back to step 1, clear pasted data and parsed data so user can paste fresh data.
   */
  const prevStep = () => {
    // When going back to step 1 (data input step), reset all pasted/parsed data
    if (step === 2 && (mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom')) {
      // Clear pasted table data so the paste area is empty and ready for new data
      setPastedTableData('');
      setShowPasteInput(true);
      // Clear parsed data and columns so label options dropdown doesn't show old data
      setCustomData(null);
      setColumns([]);
      // Clear column selections that were based on old data
      setLabelColumn('');
      setValueColumn1('');
      setValueColumn2('');
      setVariable1Column('');
      setVariable2Column('');
      setVariable1ExplicitNone(false);
      setVariable2ExplicitNone(false);
      setVariable1Text('');
      setVariable2Text('');
      setStatusColumn('');
      setLatitudeColumn('');
      setLongitudeColumn('');
      setLatLongWarning('');
      setLatLongWarningAcknowledged(false);
      // Reset class break infos
      setColorClassBreakInfos(null);
      setSizeClassBreakInfos(null);
      setNumSizeBreaks(null);
      setNumColorBreaks(null);
      // Clear file input if it exists
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    setStep(step - 1);
    setFileError('');
  };
  
    const updateLabelOption = (option, value) => setLabelOptions(prev => ({ ...prev, [option]: value }));

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Create New Map</h2>
          <button onClick={onCancel} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" disabled={isSaving}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
          {/* Enhanced step indicator that adapts to map type */}
          <div className="flex mb-6 border-b border-gray-200 dark:border-gray-700">
            {/* Step 1: Map Type - Always shown */}
            <div className={`pb-2 px-4 border-b-2 ${step >= 1 ? 'border-blue-500 text-blue-500' : 'border-transparent text-gray-500'}`}>
              1. Map Type
            </div>

            {/* Step 2: Configuration - Always shown (all map types now have 3 steps) */}
            <div className={`pb-2 px-4 border-b-2 ${step >= 2 ? 'border-blue-500 text-blue-500' : 'border-transparent text-gray-500'}`}>
              2. Configuration
            </div>

            {/* Step 3: Review */}
            <div className={`pb-2 px-4 border-b-2 ${step >= 3 ? 'border-blue-500 text-blue-500' : 'border-transparent text-gray-500'}`}>
              3. Review
            </div>
          </div>
          
          {step === 1 && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Map Name</label>
                <input type="text" value={mapName} onChange={(e) => setMapName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" placeholder="Enter map name (or leave blank for auto-title)" disabled={isSaving} />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Map Type</label>
                
                {/* Enabled Map Types */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {MAP_TYPE_DEFINITIONS.filter(type => type.enabled).map(type => (
                    <div 
                      key={type.id} 
                      className={`border rounded-lg p-4 transition-colors 
                        ${isSaving 
                          ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-700/50'
                          : mapType === type.id 
                            ? 'cursor-pointer border-blue-500 bg-blue-50 dark:bg-blue-900/30' 
                            : 'cursor-pointer border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                        }
                      `}
                      onClick={() => !isSaving && setMapType(type.id)}
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{type.name}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{type.desc}</div>
                    </div>
                  ))}
                </div>

                {/* Disabled Map Types */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {MAP_TYPE_DEFINITIONS.filter(type => !type.enabled).map(type => (
                    <div 
                      key={type.id} 
                      className="border rounded-lg p-4 transition-colors border-gray-300 bg-gray-100 dark:bg-gray-800 dark:border-gray-600 opacity-50 cursor-not-allowed"
                    >
                      <div className="font-medium text-gray-400 dark:text-gray-500">{type.name}</div>
                      <div className="text-sm text-gray-400 dark:text-gray-500">{type.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {(mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Upload Data File</label>

                  {/* Toggle between paste and file upload */}
                  <div className="mb-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowPasteInput(true)}
                      className={`px-4 py-2 text-sm font-medium rounded-md ${
                        showPasteInput
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                      disabled={isSaving}
                    >
                      Paste Table
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPasteInput(false)}
                      className={`px-4 py-2 text-sm font-medium rounded-md ${
                        !showPasteInput
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                      disabled={isSaving}
                    >
                      Upload File
                    </button>
                  </div>

                  {mapType === 'pipeline' && (
                    <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                      Required columns: "Latitude", "Longitude", and "Status"
                    </p>
                  )}

                  {(mapType === 'comps' || mapType === '1varmap' || mapType === 'custom') && (
                    <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
                      Required columns: "Latitude" and "Longitude"
                    </p>
                  )}

                  {!showPasteInput ? (
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                      <div className="space-y-1 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" /></svg>
                        <div className="flex text-sm text-gray-600 dark:text-gray-300">
                          <label htmlFor="file-upload" className={`relative cursor-pointer bg-white dark:bg-gray-800 rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}><span>Upload a file</span><input id="file-upload" name="file-upload" type="file" accept=".csv,.xlsx,.xls" className="sr-only" onChange={handleFileUpload} ref={fileInputRef} disabled={isSaving} /></label>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">CSV, XLSX, or XLS up to 10MB</p>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-1">
                      <textarea
                        value={pastedTableData}
                        onChange={(e) => setPastedTableData(e.target.value)}
                        placeholder="Paste your tab-separated table data here (including headers)...&#10;&#10;Example:&#10;Comp Name&#9;Submarket&#9;Address&#9;City&#9;Zip&#9;Latitude&#9;Longitude&#9;TotalUnits...&#10;Wingspan&#9;Central Ogden&#9;2405 Hinckley Dr&#9;West Haven&#9;84401&#9;41.197022&#9;-112.038148&#9;352..."
                        className="w-full h-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                        disabled={isSaving}
                      />
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        Paste tab-separated data (e.g., from Excel or Google Sheets). Include the header row with column names. Click "Next" when ready.
                      </p>
                    </div>
                  )}

                  {customData && (<div className="mt-2 text-sm text-green-600 dark:text-green-400">✓ Data loaded successfully ({customData.length} rows) - Breaks will be automatically optimized for {customData.length} data points</div>)}
                  {latLongWarning && (
                    <div className="mt-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-md border border-amber-200 dark:border-amber-700">
                      <div className="flex items-start justify-between gap-3">
                        <span>⚠️ {latLongWarning}</span>
                        {!latLongWarningAcknowledged && (
                          <button
                            type="button"
                            onClick={() => setLatLongWarningAcknowledged(true)}
                            className="shrink-0 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-800 hover:bg-amber-200 dark:hover:bg-amber-700 rounded border border-amber-300 dark:border-amber-600 transition-colors"
                          >
                            Continue Anyway
                          </button>
                        )}
                        {latLongWarningAcknowledged && (
                          <span className="shrink-0 text-xs text-green-600 dark:text-green-400 font-medium">✓ Acknowledged</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Area Type Selection for Geography Maps - Required before proceeding */}
              {mapType === 'geography' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Area Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={geographyAreaType}
                    onChange={(e) => {
                      setGeographyAreaType(e.target.value);
                      setGeographyCSVFile(null);
                      setGeographyAreas([]);
                      setGeographyAreaValues({});
                      setFileError('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    disabled={isSaving}
                  >
                    <option value="">Select area type...</option>
                    <option value="zip">Zip Code</option>
                    <option value="tract">Census Tract</option>
                    <option value="blockgroup">Census Block Group</option>
                    <option value="county">County</option>
                    <option value="place">Place</option>
                    <option value="cbsa">CBSA</option>
                    <option value="state">State</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the geographic area type for your data. This determines the template format.
                  </p>
                </div>
              )}
              {fileError && (<div className="text-red-500 mt-2 text-sm">{fileError}</div>)}
            </div>
          )}
          {step === 2 && ( <div>
              {/* Heat Map / Dot Density Configuration */}
              {isHeatmapOrDotDensity && (
                <div className="space-y-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      {mapType === 'heatmap' ? 'Heat Map Configuration' : 'Dot Density Configuration'}
                    </h3>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Create a {mapType === 'heatmap' ? 'heat map' : 'dot density map'} using areas currently visible in your map view. Select the geographic area type and enrichment variable to visualize.
                    </p>
                  </div>

                  {/* View-based configuration */}
                  <div className="space-y-6">
                      <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                        <h4 className="text-sm font-semibold text-green-900 dark:text-green-100 mb-2">
                          Map View-Based {mapType === 'heatmap' ? 'Heat Map' : 'Dot Density Map'}
                        </h4>
                        <p className="text-xs text-green-700 dark:text-green-300">
                          Select the geographic area type and enrichment variable. Click "Calculate" to see how many areas are in your current map view and the associated cost.
                        </p>
                      </div>

                      {/* Area Type Selection - Available for both heatmap and dot density */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Market Area Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={geographyAreaType}
                          onChange={(e) => {
                            setGeographyAreaType(e.target.value);
                            setViewAreaCount(null);
                            setViewCost(null);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          disabled={isSaving || isCalculating}
                        >
                          <option value="">Select area type...</option>
                          <option value="county">County</option>
                          <option value="zip">Zip Code</option>
                          <option value="tract">Census Tract</option>
                        </select>
                      </div>

                      {/* Variable Selection - Single for Heatmap, Multiple for Dot Density */}
                      {mapType === 'heatmap' ? (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Enrichment Variable <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={geographyVariable}
                            onChange={(e) => setGeographyVariable(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            disabled={isSaving || isCalculating}
                          >
                            <option value="">Select a variable...</option>
                            {Object.entries(categorizedVisualizationOptions).map(([category, options]) => (
                              <optgroup label={category} key={category}>
                                {options.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </div>
                      ) : (
                        /* Multi-variable selection for Dot Density */
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Enrichment Variables <span className="text-red-500">*</span>
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                if (dotDensityVariables.length < 15) {
                                  setDotDensityVariables([...dotDensityVariables, {
                                    field: '',
                                    color: DOT_DENSITY_COLORS[dotDensityVariables.length % DOT_DENSITY_COLORS.length],
                                    label: ''
                                  }]);
                                }
                              }}
                              disabled={dotDensityVariables.length >= 15 || isSaving || isCalculating}
                              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                              + Add Variable
                            </button>
                          </div>

                          {dotDensityVariables.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                              Click "Add Variable" to select enrichment variables for dot density visualization.
                            </p>
                          )}

                          {dotDensityVariables.map((variable, index) => (
                            <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                              <input
                                type="color"
                                value={variable.color}
                                onChange={(e) => {
                                  const updated = [...dotDensityVariables];
                                  updated[index].color = e.target.value;
                                  setDotDensityVariables(updated);
                                }}
                                className="w-10 h-10 rounded cursor-pointer border-0"
                                title="Select color for this variable"
                              />
                              <select
                                value={variable.field}
                                onChange={(e) => {
                                  const updated = [...dotDensityVariables];
                                  updated[index].field = e.target.value;
                                  // Find the label for the selected field
                                  const selectedOption = Object.values(categorizedVisualizationOptions)
                                    .flat()
                                    .find(opt => opt.value === e.target.value);
                                  updated[index].label = selectedOption?.label || e.target.value;
                                  setDotDensityVariables(updated);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                disabled={isSaving || isCalculating}
                              >
                                <option value="">Select a variable...</option>
                                {Object.entries(categorizedVisualizationOptions).map(([category, options]) => (
                                  <optgroup label={category} key={category}>
                                    {options.map((option) => (
                                      <option
                                        key={option.value}
                                        value={option.value}
                                        disabled={dotDensityVariables.some((v, i) => i !== index && v.field === option.value)}
                                      >
                                        {option.label}
                                      </option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => {
                                  setDotDensityVariables(dotDensityVariables.filter((_, i) => i !== index));
                                }}
                                className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                title="Remove variable"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          ))}

                          {dotDensityVariables.length > 0 && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {dotDensityVariables.length}/15 variables selected. Each variable will be shown as dots with its assigned color.
                            </p>
                          )}
                        </div>
                      )}

                      {/* Calculate Button and Cost Display */}
                      <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={async () => {
                            // For heatmap, check geographyVariable; for dotdensity, check dotDensityVariables
                            const hasVariable = mapType === 'heatmap'
                              ? geographyVariable
                              : dotDensityVariables.length > 0 && dotDensityVariables.every(v => v.field);
                            if (!geographyAreaType || !hasVariable) {
                              setFileError(mapType === 'heatmap'
                                ? 'Please select both area type and enrichment variable before calculating.'
                                : 'Please select area type and at least one enrichment variable before calculating.');
                              return;
                            }
                            setFileError('');
                            setIsCalculating(true);

                            try {
                              if (!mapView || !mapView.extent) {
                                throw new Error('Map view not available. Please ensure the map is loaded.');
                              }

                              // Calculate the visible extent by accounting for UI overlays
                              // The mapView.extent includes areas covered by sidebars, toolbars, etc.
                              // We need to calculate what's actually visible to the user

                              const mapContainer = mapView.container;
                              if (!mapContainer) {
                                throw new Error('Map container not available.');
                              }

                              // Get the map container's bounding rectangle
                              const mapRect = mapContainer.getBoundingClientRect();

                              // Initialize visible bounds as the full map container
                              let visibleLeft = 0;
                              let visibleTop = 0;
                              let visibleRight = mapRect.width;
                              let visibleBottom = mapRect.height;

                              // Detect sidebar covering the right side of the map
                              // Look for common sidebar patterns: right-positioned fixed/absolute elements
                              const sidebarSelectors = [
                                '.absolute.right-0',           // Right-positioned absolute element
                                '.fixed.right-0',              // Right-positioned fixed element
                                '[class*="sidebar"]',          // Any element with "sidebar" in class
                                '[class*="Sidebar"]',          // Capitalized variant
                                '.w-\\[440px\\].right-0',      // Specific sidebar width
                                '[class*="translate-x-0"][class*="right"]'  // Visible (not translated) right panel
                              ];

                              let sidebar = null;
                              for (const selector of sidebarSelectors) {
                                try {
                                  const element = document.querySelector(selector);
                                  if (element) {
                                    const rect = element.getBoundingClientRect();
                                    // Check if this element is actually visible and on the right side
                                    if (rect.width > 100 && rect.right >= mapRect.right - 10) {
                                      sidebar = element;
                                      break;
                                    }
                                  }
                                } catch (e) {
                                  // Invalid selector, skip
                                }
                              }

                              if (sidebar) {
                                const sidebarRect = sidebar.getBoundingClientRect();
                                // Check if sidebar overlaps with the map
                                if (sidebarRect.left < mapRect.right && sidebarRect.right > mapRect.left) {
                                  // Sidebar is covering part of the map on the right
                                  const sidebarWidthOverMap = Math.min(mapRect.right, sidebarRect.right) - Math.max(mapRect.left, sidebarRect.left);
                                  if (sidebarWidthOverMap > 0) {
                                    visibleRight = mapRect.width - sidebarWidthOverMap;
                                    console.log(`[NewMapDialog] Sidebar detected, reducing visible width by ${sidebarWidthOverMap}px`);
                                  }
                                }
                              }

                              // Detect any left-side panels or toolbars
                              const leftPanel = document.querySelector('.absolute.left-0[class*="w-"], [class*="panel"][class*="left"]');
                              if (leftPanel) {
                                const leftPanelRect = leftPanel.getBoundingClientRect();
                                if (leftPanelRect.right > mapRect.left && leftPanelRect.left < mapRect.right) {
                                  const panelWidthOverMap = Math.min(leftPanelRect.right, mapRect.right) - mapRect.left;
                                  if (panelWidthOverMap > 0) {
                                    visibleLeft = panelWidthOverMap;
                                    console.log(`[NewMapDialog] Left panel detected, offsetting visible left by ${panelWidthOverMap}px`);
                                  }
                                }
                              }

                              // Convert screen coordinates to map coordinates
                              // toMap returns a Point object with x, y coordinates
                              const topLeftScreen = { x: visibleLeft, y: visibleTop };
                              const bottomRightScreen = { x: visibleRight, y: visibleBottom };

                              const topLeftMap = mapView.toMap(topLeftScreen);
                              const bottomRightMap = mapView.toMap(bottomRightScreen);

                              let extentData;
                              if (!topLeftMap || !bottomRightMap) {
                                console.warn('[NewMapDialog] Could not convert screen to map coordinates, using full extent');
                                // Fall back to full extent
                                const extent = mapView.extent;
                                extentData = {
                                  xmin: extent.xmin,
                                  ymin: extent.ymin,
                                  xmax: extent.xmax,
                                  ymax: extent.ymax,
                                  spatialReference: extent.spatialReference.wkid || extent.spatialReference.latestWkid
                                };
                              } else {
                                // Build extent from visible coordinates
                                // Note: In screen coords, y increases downward, but in map coords y increases upward
                                // So topLeftMap.y > bottomRightMap.y
                                extentData = {
                                  xmin: topLeftMap.x,
                                  ymin: bottomRightMap.y,  // bottom of screen = lower y in map
                                  xmax: bottomRightMap.x,
                                  ymax: topLeftMap.y,      // top of screen = higher y in map
                                  spatialReference: mapView.spatialReference.wkid || mapView.spatialReference.latestWkid
                                };

                                console.log(`[NewMapDialog] Visible extent calculated:`, {
                                  screenBounds: { left: visibleLeft, top: visibleTop, right: visibleRight, bottom: visibleBottom },
                                  mapBounds: extentData,
                                  fullExtent: { xmin: mapView.extent.xmin, xmax: mapView.extent.xmax }
                                });
                              }

                              setViewExtent(extentData);

                              const baseURL = getApiUrl();
                              const token = localStorage.getItem('accessToken');

                              // Use standalone endpoint to work around Choreo @action decorator issues
                              const response = await axios.get(
                                `${baseURL}/api/count-areas-in-extent/`,
                                {
                                  params: {
                                    extent: JSON.stringify(extentData),
                                    area_type: geographyAreaType
                                  },
                                  headers: {
                                    'Authorization': token ? `Bearer ${token}` : ''
                                  }
                                }
                              );

                              setViewAreaCount(response.data.count);
                              // Cost = areas * variables * $0.001
                              // For heatmap: 1 variable, for dot density: number of selected variables
                              const numVariables = mapType === 'dotdensity' ? dotDensityVariables.length : 1;
                              setViewCost(response.data.count * numVariables * 0.001);
                            } catch (error) {
                              console.error('Error calculating areas:', error);
                              // Detailed debugging for 405 errors
                              console.error('[DEBUG] Full error object:', {
                                message: error.message,
                                status: error.response?.status,
                                statusText: error.response?.statusText,
                                responseData: error.response?.data,
                                responseHeaders: error.response?.headers,
                                requestURL: error.config?.url,
                                requestMethod: error.config?.method,
                                requestHeaders: error.config?.headers,
                              });
                              if (error.response?.status === 405) {
                                console.error('[DEBUG 405] Method Not Allowed - This usually means:');
                                console.error('  1. The server received the request but rejected the HTTP method (POST)');
                                console.error('  2. Possible causes: proxy redirect converting POST to GET, or endpoint not deployed');
                                console.error('  3. Check "Allow" header in response:', error.response?.headers?.allow);
                              }
                              const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message;
                              setFileError(`Error: ${errorMessage}`);
                              setViewAreaCount(null);
                              setViewCost(null);
                            } finally {
                              setIsCalculating(false);
                            }
                          }}
                          disabled={!geographyAreaType || (mapType === 'heatmap' ? !geographyVariable : dotDensityVariables.length === 0 || !dotDensityVariables.every(v => v.field)) || isSaving || isCalculating}
                          className={`w-full px-4 py-3 rounded-md font-medium transition-colors duration-200 flex items-center justify-center gap-2 ${
                            !geographyAreaType || (mapType === 'heatmap' ? !geographyVariable : dotDensityVariables.length === 0 || !dotDensityVariables.every(v => v.field)) || isSaving || isCalculating
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {isCalculating ? (
                            <>
                              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Calculating...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              Calculate Area Count & Cost
                            </>
                          )}
                        </button>

                        {viewAreaCount !== null && viewCost !== null && (
                          <div className={`mt-4 p-4 rounded-lg border ${viewAreaCount > 2000 ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-600' : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600'}`}>
                            <div className="space-y-2">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Areas in Current View:
                                </span>
                                <span className={`text-lg font-bold ${viewAreaCount > 2000 ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                                  {viewAreaCount.toLocaleString()}
                                </span>
                              </div>
                              {viewAreaCount > 2000 && (
                                <div className="flex items-start gap-2 p-2 bg-red-100 dark:bg-red-900/30 rounded text-red-700 dark:text-red-300">
                                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-sm">
                                    Maximum is 2,000 areas. Please zoom in or select a smaller region.
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Estimated Cost:
                                </span>
                                <span className="text-xl font-bold text-green-600 dark:text-green-400">
                                  ${viewCost.toFixed(2)}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                                {mapType === 'dotdensity' ? (
                                  <>
                                    {viewAreaCount.toLocaleString()} areas × {dotDensityVariables.length} variable{dotDensityVariables.length !== 1 ? 's' : ''} × $0.001 = ${viewCost.toFixed(2)}
                                  </>
                                ) : (
                                  <>
                                    Calculated at $0.001 per {geographyAreaType === 'zip' ? 'zip code' : geographyAreaType === 'tract' ? 'census tract' : 'county'}
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  {fileError && (<div className="text-red-500 mt-2 text-sm">{fileError}</div>)}
                </div>
              )}

              {/* Geography Map Configuration (value-based only) */}
              {isGeographyMap && (
                <div className="space-y-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      Map by Geography (Value-based)
                    </h3>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                      Upload a CSV file with geographic area identifiers and values. The heatmap will be generated using your uploaded values - no enrichment required.
                    </p>
                  </div>


                  {/* Template Download - Only show for value-based geography */}
                  {mapType === 'geography' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                          📄 Need a template?
                        </h4>
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          Download our simple template to get started quickly
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          // Generate Excel template with auto-sized columns
                          const workbook = XLSX.utils.book_new();

                          // Different templates for value-based vs enrichment-based
                          let instructionsData, dataSheet;

                          // Get current area type for template generation
                          const currentAreaType = geographyAreaType || 'zip';

                          // Area-type specific templates
                          // Column layout: [Area Type], Value, [State], [County], Label (Optional)
                          // Label is always last column
                          const isValueBased = mapType === 'geography';

                          // Define templates based on area type requirements
                          if (currentAreaType === 'zip') {
                            // Zip Code: No State or County required
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - Zip Code - Instructions`],
                                [''],
                                ['1. Add your zip codes and values in the "Data" sheet'],
                                ['2. Each row should contain ONE 5-digit zip code and its value'],
                                ['3. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['Zip Code', 'Value', 'Label (Optional)'],
                                ['90210', '1234.56', 'Beverly Hills'],
                                ['90211', '2345.67', ''],
                                ['10001', '3456.78', 'Manhattan'],
                              ];
                              dataSheet = [['Zip Code', 'Value', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - Zip Code - Instructions`],
                                [''],
                                ['1. Add your zip codes in the "Data" sheet'],
                                ['2. List multiple 5-digit zip codes separated by commas'],
                                [''],
                                ['Examples:'],
                                ['Zip Code'],
                                ['90210, 90211, 90212'],
                                ['10001, 10002'],
                              ];
                              dataSheet = [['Zip Code']];
                            }
                          } else if (currentAreaType === 'tract') {
                            // Census Tract: State and County REQUIRED
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - Census Tract - Instructions`],
                                [''],
                                ['1. Add your census tracts and values in the "Data" sheet'],
                                ['2. Each row should contain ONE 6-digit tract code and its value'],
                                ['3. State and County columns are REQUIRED'],
                                ['4. Format tracts as 6 digits (e.g., 006101)'],
                                ['5. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['Census Tract', 'Value', 'State', 'County', 'Label (Optional)'],
                                ['006101', '1234.56', 'CA', 'Los Angeles', 'Downtown'],
                                ['006102', '2345.67', 'CA', 'Los Angeles', ''],
                                ['000100', '3456.78', 'NY', 'New York', 'Midtown'],
                              ];
                              dataSheet = [['Census Tract', 'Value', 'State', 'County', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - Census Tract - Instructions`],
                                [''],
                                ['1. Add your census tracts in the "Data" sheet'],
                                ['2. List multiple 6-digit tract codes separated by commas'],
                                ['3. State and County columns are REQUIRED'],
                                [''],
                                ['Examples:'],
                                ['Census Tract', 'State', 'County'],
                                ['006101, 006102', 'CA', 'Los Angeles'],
                                ['000100, 000200', 'NY', 'New York'],
                              ];
                              dataSheet = [['Census Tract', 'State', 'County']];
                            }
                          } else if (currentAreaType === 'blockgroup') {
                            // Census Block Group: State and County REQUIRED
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - Block Group - Instructions`],
                                [''],
                                ['1. Add your block groups and values in the "Data" sheet'],
                                ['2. Each row should contain ONE block group code (tract + block group digit)'],
                                ['3. State and County columns are REQUIRED'],
                                ['4. Format as 7 digits: 6-digit tract + 1-digit block group (e.g., 0061011)'],
                                ['5. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['Block Group', 'Value', 'State', 'County', 'Label (Optional)'],
                                ['0061011', '1234.56', 'CA', 'Los Angeles', 'Area 1'],
                                ['0061012', '2345.67', 'CA', 'Los Angeles', ''],
                                ['0001001', '3456.78', 'NY', 'New York', 'Area 2'],
                              ];
                              dataSheet = [['Block Group', 'Value', 'State', 'County', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - Block Group - Instructions`],
                                [''],
                                ['1. Add your block groups in the "Data" sheet'],
                                ['2. List multiple block group codes separated by commas'],
                                ['3. State and County columns are REQUIRED'],
                                [''],
                                ['Examples:'],
                                ['Block Group', 'State', 'County'],
                                ['0061011, 0061012', 'CA', 'Los Angeles'],
                                ['0001001, 0001002', 'NY', 'New York'],
                              ];
                              dataSheet = [['Block Group', 'State', 'County']];
                            }
                          } else if (currentAreaType === 'county') {
                            // County: State REQUIRED
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - County - Instructions`],
                                [''],
                                ['1. Add your counties and values in the "Data" sheet'],
                                ['2. Each row should contain ONE county name and its value'],
                                ['3. State column is REQUIRED'],
                                ['4. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['County', 'Value', 'State', 'Label (Optional)'],
                                ['Los Angeles', '1234.56', 'CA', 'LA County'],
                                ['Orange', '2345.67', 'CA', ''],
                                ['Cook', '3456.78', 'IL', 'Chicago Area'],
                              ];
                              dataSheet = [['County', 'Value', 'State', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - County - Instructions`],
                                [''],
                                ['1. Add your counties in the "Data" sheet'],
                                ['2. List multiple county names separated by commas'],
                                ['3. State column is REQUIRED'],
                                [''],
                                ['Examples:'],
                                ['County', 'State'],
                                ['Los Angeles, Orange', 'CA'],
                                ['Cook, DuPage', 'IL'],
                              ];
                              dataSheet = [['County', 'State']];
                            }
                          } else if (currentAreaType === 'place') {
                            // Place: State REQUIRED
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - Place - Instructions`],
                                [''],
                                ['1. Add your places and values in the "Data" sheet'],
                                ['2. Each row should contain ONE place name and its value'],
                                ['3. State column is REQUIRED'],
                                ['4. Include "city" suffix for cities (e.g., "Los Angeles city")'],
                                ['5. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['Place', 'Value', 'State', 'Label (Optional)'],
                                ['Los Angeles city', '1234.56', 'CA', 'LA'],
                                ['Beverly Hills city', '2345.67', 'CA', ''],
                                ['Chicago city', '3456.78', 'IL', 'Chicago'],
                              ];
                              dataSheet = [['Place', 'Value', 'State', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - Place - Instructions`],
                                [''],
                                ['1. Add your places in the "Data" sheet'],
                                ['2. List multiple place names separated by commas'],
                                ['3. State column is REQUIRED'],
                                ['4. Include "city" suffix for cities'],
                                [''],
                                ['Examples:'],
                                ['Place', 'State'],
                                ['Los Angeles city, Beverly Hills city', 'CA'],
                                ['Chicago city, Naperville city', 'IL'],
                              ];
                              dataSheet = [['Place', 'State']];
                            }
                          } else if (currentAreaType === 'cbsa') {
                            // CBSA: No State or County required
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - CBSA - Instructions`],
                                [''],
                                ['1. Add your CBSAs and values in the "Data" sheet'],
                                ['2. Each row should contain ONE CBSA name or code and its value'],
                                ['3. Use full CBSA names or numeric codes'],
                                ['4. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['CBSA', 'Value', 'Label (Optional)'],
                                ['Los Angeles-Long Beach-Anaheim, CA', '1234.56', 'LA Metro'],
                                ['31080', '2345.67', ''],
                                ['New York-Newark-Jersey City, NY-NJ-PA', '3456.78', 'NYC Metro'],
                              ];
                              dataSheet = [['CBSA', 'Value', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - CBSA - Instructions`],
                                [''],
                                ['1. Add your CBSAs in the "Data" sheet'],
                                ['2. List multiple CBSA names or codes separated by commas'],
                                [''],
                                ['Examples:'],
                                ['CBSA'],
                                ['Los Angeles-Long Beach-Anaheim, CA'],
                                ['31080, 35620'],
                              ];
                              dataSheet = [['CBSA']];
                            }
                          } else if (currentAreaType === 'state') {
                            // State: No additional columns required (use "State Name" to avoid confusion)
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - State - Instructions`],
                                [''],
                                ['1. Add your states and values in the "Data" sheet'],
                                ['2. Each row should contain ONE state name or abbreviation and its value'],
                                ['3. Use full state names or 2-letter abbreviations'],
                                ['4. Label column is optional - used for custom labels on the map'],
                                [''],
                                ['Examples:'],
                                ['State', 'Value', 'Label (Optional)'],
                                ['California', '1234.56', 'CA'],
                                ['TX', '2345.67', ''],
                                ['New York', '3456.78', 'NY'],
                              ];
                              dataSheet = [['State', 'Value', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - State - Instructions`],
                                [''],
                                ['1. Add your states in the "Data" sheet'],
                                ['2. List multiple state names or abbreviations separated by commas'],
                                [''],
                                ['Examples:'],
                                ['State'],
                                ['California, Nevada, Arizona'],
                                ['CA, NV, AZ'],
                              ];
                              dataSheet = [['State']];
                            }
                          } else {
                            // Fallback to zip template
                            if (isValueBased) {
                              instructionsData = [
                                [`Geography Map Template - Instructions`],
                                [''],
                                ['1. Add your areas and values in the "Data" sheet'],
                                [''],
                                ['Examples:'],
                                ['Zip Code', 'Value', 'Label (Optional)'],
                                ['90210', '1234.56', 'Beverly Hills'],
                              ];
                              dataSheet = [['Zip Code', 'Value', 'Label (Optional)']];
                            } else {
                              instructionsData = [
                                [`Geography Map Template - Instructions`],
                                [''],
                                ['1. Add your areas in the "Data" sheet'],
                                [''],
                                ['Examples:'],
                                ['Zip Code'],
                                ['90210, 90211'],
                              ];
                              dataSheet = [['Zip Code']];
                            }
                          }

                          // Convert to worksheets
                          const instructionsWS = XLSX.utils.aoa_to_sheet(instructionsData);
                          const dataWS = XLSX.utils.aoa_to_sheet(dataSheet);

                          // Set column widths for instructions sheet
                          instructionsWS['!cols'] = [
                            { wch: 55 }, // Wide column for instructions
                            { wch: 40 },
                            { wch: 15 },
                            { wch: 15 },
                            { wch: 20 },
                          ];

                          // Set column widths for data sheet based on number of columns
                          const numCols = dataSheet[0].length;
                          if (numCols === 1) {
                            // Single column (enrichment templates)
                            dataWS['!cols'] = [
                              { wch: 40 }, // Area Type
                            ];
                          } else if (numCols === 2) {
                            // County/Place enrichment: Area Type, State
                            dataWS['!cols'] = [
                              { wch: 35 }, // Area Type
                              { wch: 10 }, // State
                            ];
                          } else if (numCols === 3) {
                            // Zip/CBSA/State value-based: Area Type, Value, Label
                            // OR Tract/BlockGroup enrichment: Area Type, State, County
                            dataWS['!cols'] = [
                              { wch: 35 }, // Area Type
                              { wch: 15 }, // Value or State
                              { wch: 20 }, // Label or County
                            ];
                          } else if (numCols === 4) {
                            // County/Place value-based: Area Type, Value, State, Label
                            dataWS['!cols'] = [
                              { wch: 25 }, // Area Type
                              { wch: 15 }, // Value
                              { wch: 10 }, // State
                              { wch: 20 }, // Label
                            ];
                          } else if (numCols === 5) {
                            // Tract/BlockGroup value-based: Area Type, Value, State, County, Label
                            dataWS['!cols'] = [
                              { wch: 20 }, // Area Type
                              { wch: 15 }, // Value
                              { wch: 10 }, // State
                              { wch: 20 }, // County
                              { wch: 20 }, // Label
                            ];
                          }

                          // Add worksheets to workbook
                          XLSX.utils.book_append_sheet(workbook, instructionsWS, 'Instructions');
                          XLSX.utils.book_append_sheet(workbook, dataWS, 'Data');

                          // Generate Excel file
                          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
                          const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                          const link = document.createElement('a');
                          const url = URL.createObjectURL(blob);
                          link.setAttribute('href', url);
                          link.setAttribute('download', `geography_${currentAreaType}_template.xlsx`);
                          link.style.visibility = 'hidden';
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                        className="ml-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download Template
                      </button>
                    </div>
                  </div>
                  )}

                  {/* Data Input (Paste or Upload) - Only for value-based geography */}
                  {mapType === 'geography' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Data Input <span className="text-red-500">*</span>
                    </label>

                    {/* Toggle between paste and file upload */}
                    <div className="mb-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowGeographyPasteInput(true)}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          showGeographyPasteInput
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                        disabled={isSaving}
                      >
                        Paste Table
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowGeographyPasteInput(false)}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${
                          !showGeographyPasteInput
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                        disabled={isSaving}
                      >
                        Upload File
                      </button>
                    </div>

                    {/* Paste Table Input */}
                    {showGeographyPasteInput ? (
                      <div>
                        <textarea
                          value={geographyPastedData}
                          onChange={(e) => {
                            const pastedText = e.target.value;
                            setGeographyPastedData(pastedText);
                            setFileError('');

                            // Parse pasted data in real-time
                            // New column structure: [Area Type], Value, [State], [County], Label (Optional)
                            if (pastedText.trim()) {
                              const lines = pastedText.trim().split('\n').filter(line => line.trim());
                              if (lines.length >= 1) {
                                // Check if first line looks like headers
                                const firstRowCells = lines[0].split('\t').map(h => h.trim().toLowerCase());
                                const hasHeaders = firstRowCells.some(cell =>
                                  cell.includes('zip') || cell.includes('tract') || cell.includes('county') ||
                                  cell.includes('place') || cell.includes('cbsa') || cell.includes('state') ||
                                  cell.includes('block') || cell === 'value' || cell === 'values'
                                );

                                let areaColIndex = 0; // Area type is always first column
                                let valueColIndex = 1; // Value is always second column
                                let startRow = hasHeaders ? 1 : 0;

                                const areas = [];
                                const areaValues = {};

                                // Parse data rows
                                for (let i = startRow; i < lines.length; i++) {
                                  const cells = lines[i].split('\t');
                                  const area = cells[areaColIndex]?.trim();

                                  if (area) {
                                    if (mapType === 'geography') {
                                      // Value-based: one area per row with value in column 1
                                      const value = parseFloat(cells[valueColIndex]);
                                      if (!isNaN(value)) {
                                        areas.push(area);
                                        areaValues[area] = value;
                                      }
                                    } else {
                                      // Enrichment-based: split by commas
                                      const locs = area.split(',').map(loc => loc.trim()).filter(Boolean);
                                      areas.push(...locs);
                                    }
                                  }
                                }

                                setGeographyAreas(areas);
                                setGeographyAreaValues(areaValues);
                                setGeographyCSVFile({ name: 'pasted-data' }); // Mock file object for validation
                              }
                            } else {
                              setGeographyAreas([]);
                              setGeographyAreaValues({});
                              setGeographyCSVFile(null);
                            }
                          }}
                          placeholder={mapType === 'geography'
                            ? "Paste your tab-separated table data here...\n\nExample:\nZip Code\tValue\tLabel (Optional)\n90210\t1234.56\tBeverly Hills\n90211\t2345.67\t"
                            : "Paste your tab-separated table data here...\n\nExample:\nZip Code\n90210, 90211, 90212"}
                          className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono text-sm"
                          disabled={isSaving}
                        />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Paste tab-separated data (e.g., from Excel). Include the header row with column names.
                        </p>
                      </div>
                    ) : (
                      /* File Upload Input */
                      <div>
                        <input
                          type="file"
                          accept=".csv,.xlsx,.xls,.xlsm"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            setGeographyCSVFile(file);
                            setFileError('');
                            setGeographyPastedData(''); // Clear pasted data when uploading file

                            const fileExtension = file.name.split('.').pop().toLowerCase();

                        if (fileExtension === 'csv') {
                          // Parse CSV to extract area names and optionally values
                          setGeographyTypeAutoDetected(false);
                          Papa.parse(file, {
                            header: true,
                            skipEmptyLines: 'greedy',
                            comments: '#', // Skip lines starting with #
                            complete: (results) => {
                              if (results.data && results.data.length > 0) {
                                const headers = results.meta.fields;
                                const areas = [];
                                const areaValues = {};

                                // Find Locations column (could be named "Locations" or be the second column)
                                const locationsCol = headers.find(h => h.toLowerCase().includes('location')) || headers[1] || headers[0];

                                // For value-based geography maps, find Value column
                                const valueCol = headers.find(h => h.toLowerCase() === 'value' || h.toLowerCase() === 'values');

                                // Extract all location data and values
                                results.data.forEach(row => {
                                  if (row[locationsCol]) {
                                    const locationData = String(row[locationsCol]).trim();
                                    if (locationData && !locationData.startsWith('#')) {
                                      // For value-based maps, areas should be individual (one per row)
                                      if (valueCol && mapType === 'geography') {
                                        const area = locationData.trim();
                                        const value = parseFloat(row[valueCol]);
                                        if (area && !isNaN(value)) {
                                          areas.push(area);
                                          areaValues[area] = value;
                                        }
                                      } else {
                                        // For enriched maps, split by commas to get individual areas
                                        const locs = locationData.split(',').map(loc => loc.trim()).filter(Boolean);
                                        areas.push(...locs);
                                      }
                                    }
                                  }
                                });

                                setGeographyAreas(areas);
                                setGeographyAreaValues(areaValues);
                              }
                            },
                            error: (error) => {
                              setFileError(`Error parsing CSV: ${error.message}`);
                            }
                          });
                        } else if (['xlsx', 'xls', 'xlsm'].includes(fileExtension)) {
                          // Parse Excel file
                          const reader = new FileReader();
                          reader.onload = (e) => {
                            try {
                              const data = new Uint8Array(e.target.result);
                              const workbook = XLSX.read(data, { type: 'array', cellDates: true });
                              const firstSheetName = workbook.SheetNames[0];
                              const worksheet = workbook.Sheets[firstSheetName];
                              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                              // Find the header row (look for "Name", "Type", "Locations", "Value")
                              let headerRowIndex = -1;
                              let locationsColIndex = -1;
                              let typeColIndex = -1;
                              let valueColIndex = -1;

                              for (let i = 0; i < Math.min(jsonData.length, 20); i++) {
                                const row = jsonData[i];
                                if (row && row.length > 0) {
                                  const rowStr = row.map(cell => String(cell || '').toLowerCase());
                                  const locIdx = rowStr.findIndex(cell => cell.includes('location'));
                                  const typeIdx = rowStr.findIndex(cell => cell === 'type' || cell === 'ma type');
                                  const valueIdx = rowStr.findIndex(cell => cell === 'value' || cell === 'values');

                                  if (locIdx !== -1) {
                                    headerRowIndex = i;
                                    locationsColIndex = locIdx;
                                    typeColIndex = typeIdx;
                                    valueColIndex = valueIdx;
                                    break;
                                  }
                                }
                              }

                              // Extract areas from the Locations column and detect type
                              const areas = [];
                              const areaValues = {};
                              let detectedType = null;

                              if (headerRowIndex !== -1 && locationsColIndex !== -1) {
                                // Process rows after header
                                for (let i = headerRowIndex + 1; i < jsonData.length; i++) {
                                  const row = jsonData[i];
                                  if (row && row[locationsColIndex]) {
                                    const locationData = row[locationsColIndex];

                                    // Auto-detect type from first row if Type column exists
                                    if (!detectedType && typeColIndex !== -1 && row[typeColIndex]) {
                                      const typeStr = String(row[typeColIndex]).toLowerCase().trim();
                                      const typeMapping = {
                                        'zip': 'zip', 'zipcode': 'zip', 'zip code': 'zip',
                                        'county': 'county', 'cnty': 'county',
                                        'place': 'place', 'city': 'place',
                                        'tract': 'tract', 'census tract': 'tract',
                                        'blockgroup': 'blockgroup', 'block group': 'blockgroup',
                                        'cbsa': 'cbsa',
                                        'state': 'state'
                                      };
                                      detectedType = typeMapping[typeStr] || null;
                                    }

                                    // For value-based maps, handle one area per row with value
                                    if (valueColIndex !== -1 && mapType === 'geography') {
                                      const area = typeof locationData === 'string' ? locationData.trim() : String(locationData);
                                      const value = parseFloat(row[valueColIndex]);
                                      if (area && !isNaN(value)) {
                                        areas.push(area);
                                        areaValues[area] = value;
                                      }
                                    } else {
                                      // For enriched maps, handle different formats
                                      if (typeof locationData === 'string') {
                                        // Split by common delimiters
                                        const locs = locationData.split(/[,;\n]+/)
                                          .map(loc => loc.trim())
                                          .filter(Boolean);
                                        areas.push(...locs);
                                      } else if (typeof locationData === 'number') {
                                        areas.push(String(locationData));
                                      }
                                    }
                                  }
                                }
                              } else {
                                // Fallback: use first column if no header found
                                for (let i = 1; i < jsonData.length; i++) {
                                  const row = jsonData[i];
                                  if (row && row[0]) {
                                    areas.push(String(row[0]));
                                  }
                                }
                              }

                              // Set the areas and auto-detected type
                              setGeographyAreas(areas.filter(Boolean));
                              setGeographyAreaValues(areaValues);
                              if (detectedType) {
                                setGeographyAreaType(detectedType);
                                setGeographyTypeAutoDetected(true);
                                console.log(`Auto-detected area type: ${detectedType}`);
                              } else {
                                setGeographyTypeAutoDetected(false);
                              }
                            } catch (error) {
                              setFileError(`Error parsing Excel file: ${error.message}`);
                            }
                          };
                          reader.onerror = () => {
                            setFileError('Failed to read file');
                          };
                          reader.readAsArrayBuffer(file);
                        }
                      }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                          disabled={isSaving}
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Upload CSV or Excel (.xlsx, .xlsm) with area identifiers. Download the template above for a pre-formatted spreadsheet.
                        </p>
                      </div>
                    )}

                    {/* Show loaded data preview */}
                    {geographyCSVFile && geographyAreas.length > 0 && (
                      <div className="text-xs mt-2 space-y-1">
                        <p className="text-green-600 dark:text-green-400">
                          ✓ Loaded {geographyAreas.length} areas{geographyCSVFile.name !== 'pasted-data' ? ` from ${geographyCSVFile.name}` : ' from pasted data'}
                        </p>
                        {geographyAreas.length > 0 && (
                          <p className="text-gray-600 dark:text-gray-400">
                            Preview: {geographyAreas.slice(0, 5).join(', ')}
                            {geographyAreas.length > 5 && ` ... and ${geographyAreas.length - 5} more`}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Area Type Display - For value-based geography (selected in step 1) */}
                  {mapType === 'geography' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Area Type
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 dark:bg-gray-800 dark:border-gray-700 text-gray-700 dark:text-gray-300">
                        {geographyAreaType === 'zip' && 'Zip Code'}
                        {geographyAreaType === 'tract' && 'Census Tract'}
                        {geographyAreaType === 'blockgroup' && 'Census Block Group'}
                        {geographyAreaType === 'county' && 'County'}
                        {geographyAreaType === 'place' && 'Place'}
                        {geographyAreaType === 'cbsa' && 'CBSA'}
                        {geographyAreaType === 'state' && 'State'}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Selected in previous step. Go back to change.
                      </p>
                    </div>
                  )}

                  {/* Legend Configuration - Only for value-based geography maps with data loaded */}
                  {mapType === 'geography' && geographyCSVFile && geographyAreas.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Left Column: Legend Settings */}
                      <div className="space-y-4">
                        {/* Legend Title */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Legend Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={geographyLegendTitle}
                            onChange={(e) => setGeographyLegendTitle(e.target.value)}
                            placeholder="Enter legend title..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            disabled={isSaving}
                          />
                          <p className="text-xs text-gray-500 mt-1">Title displayed at the top of the map legend.</p>
                        </div>

                        {/* Number Format Options - 3 columns */}
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Format
                            </label>
                            <select
                              value={geographyNumberFormat}
                              onChange={(e) => setGeographyNumberFormat(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              disabled={isSaving}
                            >
                              <option value="number">Number</option>
                              <option value="currency">Currency ($)</option>
                              <option value="percent">Percent (%)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Decimals
                            </label>
                            <select
                              value={geographyDecimalPlaces}
                              onChange={(e) => setGeographyDecimalPlaces(parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              disabled={isSaving}
                            >
                              <option value={0}>0</option>
                              <option value={1}>1</option>
                              <option value={2}>2</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Commas
                            </label>
                            <label className="flex items-center h-[42px] cursor-pointer">
                              <input
                                type="checkbox"
                                checked={geographyUseCommas}
                                onChange={(e) => setGeographyUseCommas(e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                                disabled={isSaving}
                              />
                              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use</span>
                            </label>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 -mt-2">Configure how numbers appear in the legend.</p>
                      </div>

                      {/* Right Column: Legend Preview */}
                      <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 shadow-sm">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Legend Preview (7 color breaks)</p>
                        <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-2">
                          <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-1 border-b border-gray-200 dark:border-gray-600">
                            {geographyLegendTitle || 'Legend Title'}
                          </div>
                          <div className="space-y-1 max-h-48 overflow-y-auto">
                            {(() => {
                              // Calculate min/max from actual values
                              const values = Object.values(geographyAreaValues).filter(v => !isNaN(v));
                              if (values.length === 0) return <p className="text-xs text-gray-500">No numeric values found</p>;

                              const sortedValues = [...values].sort((a, b) => a - b);
                              const numBreaks = 7;

                              // Calculate quantile breaks (same as mapLayerUtils.js)
                              const classBreaks = [];
                              for (let i = 0; i < numBreaks; i++) {
                                const quantileIndex = Math.floor((sortedValues.length - 1) * (i + 1) / numBreaks);
                                classBreaks.push(sortedValues[quantileIndex]);
                              }

                              // Helper function to format numbers
                              const formatNum = (n) => {
                                let formatted;
                                if (geographyUseCommas) {
                                  formatted = n.toLocaleString(undefined, {
                                    minimumFractionDigits: geographyDecimalPlaces,
                                    maximumFractionDigits: geographyDecimalPlaces
                                  });
                                } else {
                                  formatted = n.toFixed(geographyDecimalPlaces);
                                }

                                // Apply format type prefix/suffix
                                if (geographyNumberFormat === 'currency') {
                                  return '$' + formatted;
                                } else if (geographyNumberFormat === 'percent') {
                                  return formatted + '%';
                                }
                                return formatted;
                              };

                              return GEOGRAPHY_COLOR_SCHEME.colors.map((color, index) => {
                                // Generate label: "Less than X" for lowest, "X or More" for highest, "X - Y" for middle
                                let label;
                                if (index === 0) {
                                  label = `Less than ${formatNum(classBreaks[0])}`;
                                } else if (index === numBreaks - 1) {
                                  label = `${formatNum(classBreaks[index - 1])} or More`;
                                } else {
                                  label = `${formatNum(classBreaks[index - 1])} - ${formatNum(classBreaks[index])}`;
                                }

                                return (
                                  <div key={index} className="flex items-center space-x-2">
                                    <div
                                      style={{
                                        width: '20px',
                                        height: '12px',
                                        backgroundColor: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`,
                                        border: '1px solid rgba(0,0,0,0.2)',
                                        flexShrink: 0
                                      }}
                                    />
                                    <span className="text-xs text-gray-700 dark:text-gray-300">{label}</span>
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Important Notes Section - Dynamic based on area type */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                    <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-2">
                      📋 Important Data Formatting Notes
                    </h4>
                    <div className="text-xs text-amber-800 dark:text-amber-200 space-y-2">
                      {/* Zip Code notes */}
                      {geographyAreaType === 'zip' && (
                        <div>
                          <span className="font-semibold">Zip Code Formatting:</span>
                          <br />
                          • Enter 5-digit zip codes (e.g., 90210, 10001)
                          <br />
                          • Format as text if leading zeros are needed
                        </div>
                      )}

                      {/* Census Tract notes */}
                      {geographyAreaType === 'tract' && (
                        <>
                          <div>
                            <span className="font-semibold">State:</span> Required
                            <br />
                            <span className="font-semibold">County:</span> Required
                          </div>
                          <div className="pt-1 border-t border-amber-300 dark:border-amber-700">
                            <span className="font-semibold">Tract Formatting:</span>
                            <br />
                            • Input 6 digits for Tracts (e.g., 006101)
                            <br />
                            • Format Tracts as text to ensure all 6 digits are recognized
                            <br />
                            • Excel formula: =TEXT([cell],"000000")
                          </div>
                        </>
                      )}

                      {/* Census Block Group notes */}
                      {geographyAreaType === 'blockgroup' && (
                        <>
                          <div>
                            <span className="font-semibold">State:</span> Required
                            <br />
                            <span className="font-semibold">County:</span> Required
                          </div>
                          <div className="pt-1 border-t border-amber-300 dark:border-amber-700">
                            <span className="font-semibold">Block Group Formatting:</span>
                            <br />
                            • Input 7 digits: 6-digit tract + 1-digit block group (e.g., 0061011)
                            <br />
                            • Format as text to ensure all digits are recognized
                            <br />
                            • Excel formula: =TEXT([cell],"0000000")
                          </div>
                        </>
                      )}

                      {/* County notes */}
                      {geographyAreaType === 'county' && (
                        <div>
                          <span className="font-semibold">State:</span> Required
                          <br />
                          <br />
                          <span className="font-semibold">County Definition:</span>
                          <br />
                          • Enter county name (e.g., "Los Angeles", "Cook")
                          <br />
                          • Do not include "County" suffix
                        </div>
                      )}

                      {/* Place notes */}
                      {geographyAreaType === 'place' && (
                        <div>
                          <span className="font-semibold">State:</span> Required
                          <br />
                          <br />
                          <span className="font-semibold">Place Definition:</span>
                          <br />
                          • Should include "city" suffix (e.g., "Los Angeles city")
                          <br />
                          • May also use "town", "village", "borough", etc. as appropriate
                        </div>
                      )}

                      {/* CBSA notes */}
                      {geographyAreaType === 'cbsa' && (
                        <div>
                          <span className="font-semibold">CBSA Definition:</span>
                          <br />
                          • Use full CBSA name (e.g., "Los Angeles-Long Beach-Anaheim, CA")
                          <br />
                          • Or use numeric CBSA code (e.g., 31080)
                        </div>
                      )}

                      {/* State notes */}
                      {geographyAreaType === 'state' && (
                        <div>
                          <span className="font-semibold">State Definition:</span>
                          <br />
                          • Use full state name (e.g., "California") or 2-letter abbreviation (e.g., "CA")
                        </div>
                      )}
                    </div>
                  </div>

                  {fileError && (
                    <div className="text-red-500 text-sm">{fileError}</div>
                  )}
                </div>
              )}

              {(mapType === 'comps' || mapType === '1varmap' || mapType === 'pipeline' || mapType === 'custom') && (
                <div> {/* Wrapper for point-data specific config, previews, and data table */}
                  <div className="space-y-4">
                    <div> {/* Configuration section - full width */}
                      {/* Variable/Value Column Selection - Grid layout for better space usage */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {(mapType === 'custom' || mapType === 'comps' || mapType === '1varmap') && (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{mapType === 'custom' ? "Variable 1 (for Color)" : "Value Column (Auto-generates breaks)"}</label>
                              <select
                                value={pendingValueColumn1 !== null ? pendingValueColumn1 : valueColumn1}
                                onChange={(e) => {
                                  if (mapType === 'comps' || mapType === '1varmap') {
                                    handleValueColumn1Select(e.target.value);
                                  } else {
                                    setValueColumn1(e.target.value);
                                    // Pre-fill color legend title for 2-variable maps
                                    if (e.target.value) {
                                      setColorLegendTitle(`Color: ${formatLegendTitle(e.target.value)}`);
                                    } else {
                                      setColorLegendTitle('');
                                    }
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                disabled={isSaving}
                              >
                                <option value="">Select a value...</option>
                                {columns.map((column) => (<option key={column} value={column}>{column}</option>))}
                              </select>
                              <p className="text-xs text-gray-500 mt-1">Used to determine point colors based on data values.</p>
                            </div>
                            {/* Legend Title Prompt - for 1varmap and comps when value column is being changed */}
                            {(mapType === 'comps' || mapType === '1varmap') && pendingValueColumn1 !== null && (
                              <div className="mt-3 p-3 border border-amber-300 dark:border-amber-600 rounded-md bg-amber-50 dark:bg-amber-900/20">
                                <label htmlFor="legendTitlePrompt" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Legend Title<span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                  id="legendTitlePrompt"
                                  type="text"
                                  value={pendingLegendTitle}
                                  onChange={(e) => setPendingLegendTitle(e.target.value)}
                                  placeholder={`e.g., ${formatLegendTitle(pendingValueColumn1) || 'Variable Name'}`}
                                  className={`mt-1 block w-full py-2 px-3 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 sm:text-sm ${
                                    !pendingLegendTitle.trim() ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-gray-600'
                                  }`}
                                  disabled={isSaving}
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  Enter a title for the legend section.
                                </p>
                                <div className="mt-3 flex space-x-2">
                                  <button
                                    onClick={applyValueColumn1Change}
                                    disabled={!pendingLegendTitle.trim() || isSaving}
                                    className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Apply
                                  </button>
                                  <button
                                    onClick={cancelValueColumn1Change}
                                    disabled={isSaving}
                                    className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                            {/* Color Legend Title - for custom 2-variable maps */}
                            {mapType === 'custom' && valueColumn1 && (
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Color Legend Title</label>
                                <input
                                  type="text"
                                  value={colorLegendTitle}
                                  onChange={(e) => setColorLegendTitle(e.target.value)}
                                  placeholder={`Color: ${formatLegendTitle(valueColumn1) || 'Variable 1'}`}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                  disabled={isSaving}
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave blank for default format.</p>
                              </div>
                            )}
                            {/* Number of Color Breaks - for custom 2-variable maps */}
                            {mapType === 'custom' && valueColumn1 && (
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Number of Color Breaks</label>
                                <select
                                  value={numColorBreaks || ''}
                                  onChange={(e) => setNumColorBreaks(e.target.value === '' ? null : parseInt(e.target.value))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                  disabled={isSaving}
                                >
                                  <option value="">Auto (based on data)</option>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                    <option key={n} value={n}>{n} {n === 1 ? 'break' : 'breaks'}</option>
                                  ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Select the number of color categories or let it auto-calculate.</p>
                              </div>
                            )}
                            {/* Color Legend Preview - directly under Variable 1 for custom maps */}
                            {mapType === 'custom' && valueColumn1 && (
                              <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 shadow-sm">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Color Legend Preview ({colorClassBreakInfos ? `${colorClassBreakInfos.length} breaks` : 'generating...'})</p>
                                {colorClassBreakInfos ? (
                                  <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-2">
                                    <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-1 border-b border-gray-200 dark:border-gray-600">
                                      {colorLegendTitle || `Color: ${formatLegendTitle(valueColumn1)}`}
                                    </div>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {colorClassBreakInfos.map((breakInfo, index) => (
                                        <div key={`color-${index}`} className="flex items-center space-x-2">
                                          <div
                                            style={{
                                              width: '12px',
                                              height: '12px',
                                              backgroundColor: Array.isArray(breakInfo.symbol.color)
                                                ? `rgba(${breakInfo.symbol.color[0]}, ${breakInfo.symbol.color[1]}, ${breakInfo.symbol.color[2]}, ${breakInfo.symbol.color[3]})`
                                                : breakInfo.symbol.color,
                                              borderRadius: '50%',
                                              border: `${breakInfo.symbol.outline.width}px solid ${Array.isArray(breakInfo.symbol.outline.color) ? `rgba(${breakInfo.symbol.outline.color.join(',')})` : breakInfo.symbol.outline.color}`,
                                              flexShrink: 0
                                            }}
                                          />
                                          <span className="text-xs text-gray-700 dark:text-gray-300">{breakInfo.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {(() => {
                                      const format = getCompFieldFormat(valueColumn1);
                                      const hasFormatting = format.prefix || format.suffix || format.multiplier !== 1;
                                      return hasFormatting ? (
                                        <div className="mt-2 pt-1 border-t border-gray-200 dark:border-gray-600">
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            Format: {format.prefix && `prefix "${format.prefix}"`}{format.prefix && format.suffix && ', '}{format.suffix && `suffix "${format.suffix}"`}{format.multiplier !== 1 && ` (×${format.multiplier})`}
                                          </span>
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : (
                                  <p className="text-xs text-yellow-600 dark:text-yellow-400">
                                    Could not generate color legend. Ensure '{valueColumn1}' has numeric data or try adjusting data.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {mapType === 'custom' && (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Variable 2 (for Size)</label>
                              <select value={valueColumn2} onChange={(e) => {
                                setValueColumn2(e.target.value);
                                // Pre-fill size legend title for 2-variable maps
                                if (e.target.value) {
                                  setSizeLegendTitle(`Size: ${formatLegendTitle(e.target.value)}`);
                                } else {
                                  setSizeLegendTitle('');
                                }
                              }} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving}>
                                <option value="">Select column...</option>
                                {columns.map((column) => (<option key={column} value={column}>{column}</option>))}
                              </select>
                              <p className="text-xs text-gray-500 mt-1">Used to determine point sizes based on data values.</p>
                            </div>
                            {/* Size Legend Title - for custom 2-variable maps */}
                            {valueColumn2 && (
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Size Legend Title</label>
                                <input
                                  type="text"
                                  value={sizeLegendTitle}
                                  onChange={(e) => setSizeLegendTitle(e.target.value)}
                                  placeholder={`Size: ${formatLegendTitle(valueColumn2) || 'Variable 2'}`}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                  disabled={isSaving}
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave blank for default format.</p>
                              </div>
                            )}
                            {/* Number of Size Breaks - for custom 2-variable maps */}
                            {valueColumn2 && (
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Number of Size Breaks</label>
                                <select
                                  value={numSizeBreaks || ''}
                                  onChange={(e) => setNumSizeBreaks(e.target.value === '' ? null : parseInt(e.target.value))}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                                  disabled={isSaving}
                                >
                                  <option value="">Auto (based on data)</option>
                                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                    <option key={n} value={n}>{n} {n === 1 ? 'break' : 'breaks'}</option>
                                  ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Select the number of size categories or let it auto-calculate.</p>
                              </div>
                            )}
                            {/* Size Legend Preview - directly under Variable 2 */}
                            {valueColumn2 && (
                              <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 shadow-sm">
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Size Legend Preview ({sizeClassBreakInfos ? `${sizeClassBreakInfos.length} breaks` : 'generating...'})</p>
                                {sizeClassBreakInfos ? (
                                  <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-2">
                                    <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-1 border-b border-gray-200 dark:border-gray-600">
                                      {sizeLegendTitle || `Size: ${formatLegendTitle(valueColumn2)}`}
                                    </div>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                      {sizeClassBreakInfos.map((breakInfo, index) => (
                                        <div key={`size-${index}`} className="flex items-center space-x-2">
                                          <div style={{ width: `${Math.min(breakInfo.size, 24)}px`, height: `${Math.min(breakInfo.size, 24)}px`, backgroundColor: '#cccccc', borderRadius: '50%', border: `1px solid #aaaaaa`, flexShrink: 0 }} />
                                          <span className="text-xs text-gray-700 dark:text-gray-300">{breakInfo.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                    {(() => {
                                      const format = getCompFieldFormat(valueColumn2);
                                      const hasFormatting = format.prefix || format.suffix || format.multiplier !== 1;
                                      return hasFormatting ? (
                                        <div className="mt-2 pt-1 border-t border-gray-200 dark:border-gray-600">
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            Format: {format.prefix && `prefix "${format.prefix}"`}{format.prefix && format.suffix && ', '}{format.suffix && `suffix "${format.suffix}"`}{format.multiplier !== 1 && ` (×${format.multiplier})`}
                                          </span>
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                ) : <p className="text-xs text-yellow-600 dark:text-yellow-400">Could not generate size legend. Ensure '{valueColumn2}' has numeric data or try adjusting data.</p>}
                              </div>
                            )}
                          </div>
                        )}
                        {mapType === 'pipeline' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status Column (for Color)</label>
                            <select
                              value={statusColumn || ""}
                              onChange={(e) => setStatusColumn(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              disabled={isSaving}
                            >
                              <option value="">Select column...</option>
                              {columns.map((column) => (
                                <option key={column} value={column}>
                                  {column}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Column containing status values. Points will be colored based on predefined status colors.</p>
                            {statusColumn && (
                              <p className="text-xs text-green-600 mt-1">✓ Auto-detected: "{statusColumn}"</p>
                            )}
                          </div>
                        )}
                        {/* Legend Title - for pipeline */}
                        {mapType === 'pipeline' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Legend Title</label>
                            <input
                              type="text"
                              value={colorLegendTitle}
                              onChange={(e) => setColorLegendTitle(e.target.value)}
                              placeholder="Project Status"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                              disabled={isSaving}
                            />
                            <p className="text-xs text-gray-500 mt-1">Title displayed at the top of the legend. Leave blank for "Project Status".</p>
                          </div>
                        )}
                        {(mapType === 'comps' || mapType === '1varmap') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Style Type</label>
                            <select value={rendererType} onChange={(e) => setRendererType(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving}>
                              <option value="classBreaks">Class Breaks (Color by Value)</option>
                              <option value="simple">Simple (Single Color)</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">{rendererType === 'classBreaks' ? 'Points will be colored based on value ranges for easy comparison.' : 'All points will use the same color regardless of value.'}</p>
                          </div>
                        )}
                        {/* Legend Title - for comps and 1varmap */}
                        {(mapType === 'comps' || mapType === '1varmap') && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Legend Title</label>
                            <input
                              type="text"
                              value={colorLegendTitle}
                              onChange={(e) => setColorLegendTitle(e.target.value)}
                              placeholder={valueColumn1 ? formatLegendTitle(valueColumn1) : 'Enter legend title...'}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                              disabled={isSaving}
                            />
                            <p className="text-xs text-gray-500 mt-1">Title displayed at the top of the legend. Leave blank for default.</p>
                          </div>
                        )}
                        {/* Number Format Options for comps/1varmap - only show when using classBreaks and not categorical */}
                        {(mapType === 'comps' || mapType === '1varmap') && rendererType === 'classBreaks' && !colorClassBreakInfos?.[0]?.isCategorical && (
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Format
                              </label>
                              <select
                                value={compNumberFormat}
                                onChange={(e) => setCompNumberFormat(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                disabled={isSaving}
                              >
                                <option value="number">Number</option>
                                <option value="currency">Currency ($)</option>
                                <option value="percent">Percent (%)</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Decimals
                              </label>
                              <select
                                value={compDecimalPlaces}
                                onChange={(e) => setCompDecimalPlaces(parseInt(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                disabled={isSaving}
                              >
                                <option value={0}>0</option>
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Commas
                              </label>
                              <label className="flex items-center h-[42px] cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={compUseCommas}
                                  onChange={(e) => setCompUseCommas(e.target.checked)}
                                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                                  disabled={isSaving}
                                />
                                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">Use</span>
                              </label>
                            </div>
                          </div>
                        )}
                        {mapType === '1varmap' && rendererType === 'classBreaks' && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Breaks</label>
                            <select value={numColorBreaks || ''} onChange={(e) => setNumColorBreaks(e.target.value === '' ? null : parseInt(e.target.value))} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving}>
                              <option value="">Auto (based on data)</option>
                              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                                <option key={n} value={n}>{n} {n === 1 ? 'break' : 'breaks'}</option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 mt-1">Select the number of color categories for your map. Auto will determine the optimal number based on your data.</p>
                          </div>
                        )}
                      </div>
                      {mapType === 'custom' && valueColumn2 && (
                        <>
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dot Size Range (pixels)</label>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Minimum Size</label>
                                <input
                                  type="number"
                                  min="1"
                                  max={maxDotSize - 1}
                                  value={minDotSize}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : parseInt(e.target.value);
                                    if (val === '' || !isNaN(val)) {
                                      setMinDotSize(val === '' ? '' : val);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (isNaN(val) || val < 1) {
                                      setMinDotSize(1);
                                    } else if (val >= maxDotSize) {
                                      setMinDotSize(maxDotSize - 1);
                                    } else {
                                      setMinDotSize(val);
                                    }
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                  disabled={isSaving}
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Maximum Size</label>
                                <input
                                  type="number"
                                  min={minDotSize + 1}
                                  max="50"
                                  value={maxDotSize}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : parseInt(e.target.value);
                                    if (val === '' || !isNaN(val)) {
                                      setMaxDotSize(val === '' ? '' : val);
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (isNaN(val) || val <= minDotSize) {
                                      setMaxDotSize(minDotSize + 1);
                                    } else if (val > 50) {
                                      setMaxDotSize(50);
                                    } else {
                                      setMaxDotSize(val);
                                    }
                                  }}
                                  onFocus={(e) => e.target.select()}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                  disabled={isSaving}
                                />
                              </div>
                            </div>
                            <div className="mt-2 flex items-center justify-between bg-gray-100 dark:bg-gray-700/50 p-2 rounded">
                              <div className="flex items-center space-x-2">
                                <div
                                  className="rounded-full bg-blue-500 border border-gray-400"
                                  style={{
                                    width: `${minDotSize}px`,
                                    height: `${minDotSize}px`,
                                  }}
                                />
                                <span className="text-xs text-gray-600 dark:text-gray-400">Min</span>
                              </div>
                              <div className="flex-1 mx-3 border-t border-dashed border-gray-400" />
                              <div className="flex items-center space-x-2">
                                <span className="text-xs text-gray-600 dark:text-gray-400">Max</span>
                                <div
                                  className="rounded-full bg-blue-500 border border-gray-400"
                                  style={{
                                    width: `${maxDotSize}px`,
                                    height: `${maxDotSize}px`,
                                  }}
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Each data point will be assigned a unique size between min and max based on its value.</p>
                          </div>
                        </>
                      )}

                      {/* Label Generation Section - 2 column layout */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Left: Label Preview and Generator */}
                        <div className="space-y-3">
                          {/* Label Generator Preview */}
                          <div className="p-3 bg-gray-100 dark:bg-gray-700/80 rounded-md">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label Preview</label>
                            <p className="text-blue-600 dark:text-blue-400 font-medium text-lg">{generateFormattedTitle()}</p>
                            {generateSampleLabel() && (
                              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                                <span className="text-gray-500 dark:text-gray-500">Example: </span>
                                {generateSampleLabel()}
                              </p>
                            )}
                          </div>

                          {/* Label Generation Tool Section */}
                          <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/40">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Use the options below to build your label from data columns and custom text.</p>

                            <div className="mb-3">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primary Label Column</label>
                              <select value={labelColumn} onChange={(e) => setLabelColumn(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving}>
                                <option value="">Select column...</option>
                                <option value="none">None (dots/colors only)</option>
                                {columns.map((column) => (<option key={column} value={column}>{column}</option>))}
                              </select>
                            </div>
                            <div className="mb-3">
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Variable 1</label>
                                <label className="flex items-center text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                                  <input type="checkbox" checked={!variable1Column} onChange={(e) => { if (e.target.checked) { setVariable1Column(''); setVariable1Text(''); setVariable1Prefix(''); setVariable1ExplicitNone(true); } else { setVariable1ExplicitNone(false); const yearBuiltCol = columns.find(col => col.toLowerCase() === 'year built' || col.toLowerCase() === 'yearbuilt' || col.toLowerCase() === 'year_built' || col.toLowerCase() === 'built'); if (yearBuiltCol) { setVariable1Column(yearBuiltCol); setVariable1Text(''); } } }} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600" disabled={isSaving} />
                                  <span className="ml-1.5">None</span>
                                </label>
                              </div>
                              <div className={`grid grid-cols-4 gap-2 ${!variable1Column ? 'opacity-50' : ''}`}>
                                <input type="text" value={variable1Prefix} onChange={(e) => setVariable1Prefix(e.target.value)} placeholder="Prefix" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving || !variable1Column} />
                                <select value={variable1Column} onChange={(e) => { setVariable1Column(e.target.value); if (e.target.value === '') { setVariable1Text(''); setVariable1Prefix(''); setVariable1ExplicitNone(true); } else { setVariable1ExplicitNone(false); } }} className="col-span-2 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving}>
                                  <option value="">None</option>
                                  {columns.map((c) => (<option key={c} value={c}>{c}</option>))}
                                </select>
                                <input type="text" value={variable1Text} onChange={(e) => setVariable1Text(e.target.value)} placeholder="Suffix" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving || !variable1Column} />
                              </div>
                            </div>
                            <div className="mb-1">
                              <div className="flex items-center justify-between mb-1">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Variable 2</label>
                                <label className="flex items-center text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                                  <input type="checkbox" checked={!variable2Column} onChange={(e) => { if (e.target.checked) { setVariable2Column(''); setVariable2Text(''); setVariable2Prefix(''); setVariable2ExplicitNone(true); } else { setVariable2ExplicitNone(false); const totalUnitsCol = columns.find(col => col.toLowerCase() === 'total units' || col.toLowerCase() === 'totalunits' || col.toLowerCase() === 'units' || (col.toLowerCase().includes('total') && col.toLowerCase().includes('unit'))); if (totalUnitsCol) { setVariable2Column(totalUnitsCol); setVariable2Text('Units'); } } }} className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600" disabled={isSaving} />
                                  <span className="ml-1.5">None</span>
                                </label>
                              </div>
                              <div className={`grid grid-cols-4 gap-2 ${!variable2Column ? 'opacity-50' : ''}`}>
                                <input type="text" value={variable2Prefix} onChange={(e) => setVariable2Prefix(e.target.value)} placeholder="Prefix" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving || !variable2Column} />
                                <select value={variable2Column} onChange={(e) => { setVariable2Column(e.target.value); if (e.target.value === '') { setVariable2Text(''); setVariable2Prefix(''); setVariable2ExplicitNone(true); } else { setVariable2ExplicitNone(false); } }} className="col-span-2 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving}>
                                  <option value="">None</option>
                                  {columns.map((c) => (<option key={c} value={c}>{c}</option>))}
                                </select>
                                <input type="text" value={variable2Text} onChange={(e) => setVariable2Text(e.target.value)} placeholder="Suffix" className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" disabled={isSaving || !variable2Column} />
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right: Label Display Options */}
                        <div className="space-y-3">
                          {labelColumn !== 'none' && (
                            <div>
                              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Label Display Options</h3>
                              <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/60 rounded-md space-y-3">
                                <div><label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Font Size: {labelOptions.fontSize}px</label><input type="range" min="8" max="14" value={labelOptions.fontSize} onChange={(e) => updateLabelOption('fontSize', parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600" disabled={isSaving}/></div>
                                <div>
                                  <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">Font Family</label>
                                  <select
                                    value={labelOptions.fontFamily || 'Arial'}
                                    onChange={(e) => updateLabelOption('fontFamily', e.target.value)}
                                    disabled={isSaving}
                                    className="w-full p-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                                  >
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Verdana">Verdana</option>
                                    <option value="Tahoma">Tahoma</option>
                                    <option value="Trebuchet MS">Trebuchet MS</option>
                                    <option value="Courier New">Courier New</option>
                                  </select>
                                </div>
                                <label className="flex items-center gap-1.5">
                                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600" checked={labelOptions.bold} onChange={(e) => updateLabelOption('bold', e.target.checked)} disabled={isSaving}/>
                                  <span className="text-sm text-gray-600 dark:text-gray-400">Bold Text</span>
                                </label>
                                <label className="flex items-center gap-1.5">
                                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600" checked={labelOptions.whiteBackground} onChange={(e) => updateLabelOption('whiteBackground', e.target.checked)} disabled={isSaving}/>
                                  <span className="text-sm text-gray-600 dark:text-gray-400">White Background</span>
                                </label>
                                <label className="flex items-center gap-1.5">
                                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600" checked={labelOptions.mask} onChange={(e) => updateLabelOption('mask', e.target.checked)} disabled={isSaving || labelOptions.whiteBackground}/>
                                  <span className="text-sm text-gray-600 dark:text-gray-400">Mask (white outline)</span>
                                </label>
                              </div>
                            </div>
                          )}

                          {/* Reverse Colors option for comps/1varmap with classBreaks (hide for categorical data) */}
                          {(mapType === 'comps' || mapType === '1varmap') && rendererType === 'classBreaks' && !colorClassBreakInfos?.[0]?.isCategorical && (
                            <div className="p-3 border border-gray-200 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700/40">
                              <label className="flex items-center gap-1.5">
                                <input
                                  type="checkbox"
                                  checked={reverseColors}
                                  onChange={(e) => setReverseColors(e.target.checked)}
                                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600"
                                  disabled={isSaving}
                                />
                                <span className="text-sm text-gray-600 dark:text-gray-400">Reverse Color Order</span>
                              </label>
                              <p className="text-xs text-gray-500 mt-1 ml-6">Flip the color ramp so the bottom color becomes the top and vice versa.</p>
                            </div>
                          )}

                          {/* Legend Preview for comps/1varmap - kept in right column */}
                          {(mapType === 'comps' || mapType === '1varmap') && valueColumn1 && rendererType === 'classBreaks' && (
                            <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 shadow-sm">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Legend Preview ({colorClassBreakInfos ? `${colorClassBreakInfos.length} ${colorClassBreakInfos[0]?.isCategorical ? 'categories' : 'breaks'}` : 'generating...'})</p>
                              {colorClassBreakInfos ? (
                                <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-2">
                                  <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-1 border-b border-gray-200 dark:border-gray-600">
                                    {colorLegendTitle || formatLegendTitle(valueColumn1)}
                                  </div>
                                  <div className="space-y-1 max-h-40 overflow-y-auto">
                                    {colorClassBreakInfos.map((breakInfo, index) => (
                                      <div key={index} className="flex items-center space-x-2">
                                        <div
                                          style={{
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: Array.isArray(breakInfo.symbol.color)
                                              ? `rgba(${breakInfo.symbol.color[0]}, ${breakInfo.symbol.color[1]}, ${breakInfo.symbol.color[2]}, ${breakInfo.symbol.color[3]})`
                                              : breakInfo.symbol.color,
                                            borderRadius: '50%',
                                            border: `${breakInfo.symbol.outline.width}px solid ${Array.isArray(breakInfo.symbol.outline.color) ? `rgba(${breakInfo.symbol.outline.color.join(',')})` : breakInfo.symbol.outline.color}`,
                                            flexShrink: 0
                                          }}
                                        />
                                        <span className="text-xs text-gray-700 dark:text-gray-300">{breakInfo.label}</span>
                                      </div>
                                    ))}
                                  </div>
                                  {(() => {
                                    // Don't show format for categorical data
                                    if (colorClassBreakInfos?.[0]?.isCategorical) return null;
                                    const format = getCompFieldFormat(valueColumn1);
                                    const hasFormatting = format.prefix || format.suffix || format.multiplier !== 1;
                                    return hasFormatting ? (
                                      <div className="mt-2 pt-1 border-t border-gray-200 dark:border-gray-600">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          Format: {format.prefix && `prefix "${format.prefix}"`}{format.prefix && format.suffix && ', '}{format.suffix && `suffix "${format.suffix}"`}{format.multiplier !== 1 && ` (×${format.multiplier})`}
                                        </span>
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              ) : <p className="text-xs text-yellow-600 dark:text-yellow-400">Could not generate legend. Ensure '{valueColumn1}' contains valid data. A single point style will be used.</p>}
                            </div>
                          )}
                          {(mapType === 'comps' || mapType === '1varmap') && rendererType === 'simple' && (
                            <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700/50">
                              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">Style Preview - Simple Renderer</h4>
                              <div className="flex items-center space-x-2">
                                <div
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    backgroundColor: '#800080',
                                    borderRadius: '50%',
                                    border: '1px solid #000000',
                                    flexShrink: 0
                                  }}
                                />
                                <span className="text-xs text-gray-700 dark:text-gray-300">All points will use this single color</span>
                              </div>
                            </div>
                          )}
                          {mapType === 'pipeline' && statusColumn && (
                            <div className="p-3 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-800 shadow-sm">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Legend Preview (using '{statusColumn}' column)</p>
                              <div className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded p-2">
                                <div className="font-semibold text-sm text-gray-900 dark:text-gray-100 mb-2 pb-1 border-b border-gray-200 dark:border-gray-600">
                                  {colorLegendTitle || 'Project Status'}
                                </div>
                                <div className="space-y-1 max-h-40 overflow-y-auto">
                                  {(() => {
                                    // All possible status colors
                                    const allStatusColors = {
                                      "Under Construction": "#BF0000",
                                      "Approved": "#FF7A0D",
                                      "Pending": "#F8F200",
                                      "Proposed": "#00BF2C",
                                      "Conceptual": "#009B9B",
                                      "Stalled": "#003380",
                                      "Inactive": "#5C00B8",
                                      "Pre-Leasing/Pre-Selling": "#D6009E",
                                      "Complete": "#AEAAAA",
                                    };
                                    // Standard order for display
                                    const statusOrder = [
                                      "Under Construction", "Approved", "Pending", "Proposed",
                                      "Conceptual", "Stalled", "Inactive", "Pre-Leasing/Pre-Selling", "Complete"
                                    ];
                                    // Get unique statuses from data
                                    const uniqueStatuses = customData && statusColumn
                                      ? [...new Set(customData.map(row => String(row[statusColumn] || '').trim()).filter(Boolean))]
                                      : [];
                                    // Sort: known statuses in order, then unknown statuses alphabetically
                                    const sortedStatuses = uniqueStatuses.sort((a, b) => {
                                      const aIdx = statusOrder.indexOf(a);
                                      const bIdx = statusOrder.indexOf(b);
                                      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
                                      if (aIdx !== -1) return -1;
                                      if (bIdx !== -1) return 1;
                                      return a.localeCompare(b);
                                    });
                                    return sortedStatuses.map((status) => (
                                      <div key={status} className="flex items-center space-x-2">
                                        <div
                                          style={{
                                            width: '12px',
                                            height: '12px',
                                            backgroundColor: allStatusColors[status] || '#FFA500',
                                            borderRadius: '50%',
                                            border: '1px solid #000000',
                                            flexShrink: 0
                                          }}
                                        />
                                        <span className="text-xs text-gray-700 dark:text-gray-300">{status}</span>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Data Preview Table - Only show for map types that need it (not comps, pipeline, 1varmap, custom/2var) */}
                  {customData && customData.length > 0 && (latitudeColumn || longitudeColumn) && !['comps', 'pipeline', '1varmap', 'custom'].includes(mapType) && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data Preview (First 5 Rows)</label>
                      <div className="border rounded-md overflow-x-auto max-h-60">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-100 dark:bg-gray-700/80">
                            <tr>
                              {labelColumn && labelColumn !== 'none' && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Label ({labelColumn})</th>}
                              {variable1Column && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Var1 ({variable1Column})</th>}
                              {variable2Column && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Var2 ({variable2Column})</th>}
                              {valueColumn1 && (mapType === 'custom' || mapType === 'comps' || mapType === '1varmap') && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Val1 ({valueColumn1})</th>}
                              {valueColumn2 && mapType === 'custom' && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Val2 ({valueColumn2})</th>}
                              {statusColumn && mapType === 'pipeline' && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status ({statusColumn})</th>}
                              {latitudeColumn && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Lat ({latitudeColumn})</th>}
                              {longitudeColumn && <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Lng ({longitudeColumn})</th>}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-900 dark:divide-gray-600">
                            {customData.slice(0, 5).map((row, index) => (
                              <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                {labelColumn && labelColumn !== 'none' && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[labelColumn] ?? '')}</td>}
                                {variable1Column && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[variable1Column] ?? '')}</td>}
                                {variable2Column && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[variable2Column] ?? '')}</td>}
                                {valueColumn1 && (mapType === 'custom' || mapType === 'comps' || mapType === '1varmap') && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[valueColumn1] ?? '')}</td>}
                                {valueColumn2 && mapType === 'custom' && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[valueColumn2] ?? '')}</td>}
                                {statusColumn && mapType === 'pipeline' && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[statusColumn] ?? '')}</td>}
                                {latitudeColumn && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[latitudeColumn] ?? '')}</td>}
                                {longitudeColumn && <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">{String(row[longitudeColumn] ?? '')}</td>}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {customData.length > 5 && (
                          <div className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700/80 border-t border-gray-200 dark:border-gray-700">
                            Showing 5 of {customData.length} rows
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {fileError && (<div className="text-red-500 mt-2 text-sm">{fileError}</div>)}
            </div>)}
          
          {/* Step 3 (or 2 for Heatmap/DotDensity): Review */}
          {step === maxSteps && ( 
            <div className="p-4 border rounded-md bg-gray-50 dark:bg-gray-700/80 dark:border-gray-600 space-y-4">
              <div><h3 className="font-medium text-gray-900 dark:text-white">Map Name:</h3><p className="text-gray-600 dark:text-gray-300">{mapName || generateFormattedTitle()}</p></div>
              <div><h3 className="font-medium text-gray-900 dark:text-white">Map Type:</h3><p className="text-gray-600 dark:text-gray-300">{MAP_TYPE_DEFINITIONS.find(mt => mt.id === mapType)?.name || mapType}</p></div>
              {(mapType === 'heatmap' || mapType === 'dotdensity') ? (
                <>
                  <div><h3 className="font-medium text-gray-900 dark:text-white">Data Source:</h3><p className="text-gray-600 dark:text-gray-300">{geographyDataSource === 'view' ? 'Current Map View' : 'Uploaded List'}</p></div>
                  <div><h3 className="font-medium text-gray-900 dark:text-white">Geography Level:</h3><p className="text-gray-600 dark:text-gray-300">{geographyAreaType || 'None selected'}</p></div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">Enrichment Variable:</h3>
                    <p className="text-gray-600 dark:text-gray-300">
                      {visualizationOptions.find(opt => opt.value === geographyVariable)?.label || geographyVariable || 'None selected'}
                    </p>
                  </div>
                  {viewAreaCount !== null && (
                    <div><h3 className="font-medium text-gray-900 dark:text-white">Estimated:</h3><p className="text-gray-600 dark:text-gray-300">{viewAreaCount} areas, ${viewCost?.toFixed(2)} cost</p></div>
                  )}
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">Data & Title</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Data Source: Custom File ({customData?.length || 0} rows)</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Formatted Title: {generateFormattedTitle()}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Break Optimization: {colorClassBreakInfos?.length || 'Auto'} color breaks{sizeClassBreakInfos?.length ? `, ${sizeClassBreakInfos.length} size breaks` : ''}</p>
                    <h3 className="font-medium text-gray-900 dark:text-white mt-2 mb-1">Location</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Lat: {latitudeColumn || <span className="italic text-gray-400">N/A</span>}, Lng: {longitudeColumn || <span className="italic text-gray-400">N/A</span>}</p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white mb-1">Data Columns</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Label: {labelColumn === 'none' ? 'None' : labelColumn || <span className="italic text-gray-400">N/A</span>}</p>
                    {mapType === 'custom' && <>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Value (Color): {valueColumn1 || <span className="italic text-gray-400">N/A</span>}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-300">Value (Size): {valueColumn2 || <span className="italic text-gray-400">N/A</span>}</p>
                    </>}
                    {(mapType === 'comps' || mapType === '1varmap') && <p className="text-sm text-gray-600 dark:text-gray-300">Value (Color): {valueColumn1 || <span className="italic text-gray-400">N/A</span>}</p>}
                    {mapType === 'pipeline' && <p className="text-sm text-gray-600 dark:text-gray-300">Status: {statusColumn || <span className="italic text-gray-400">N/A</span>}</p>}
                    <p className="text-sm text-gray-600 dark:text-gray-300">Var 1: {variable1Column ? `${variable1Prefix || ''}${variable1Column}${variable1Text || ''}` : <span className="italic text-gray-400">N/A</span>}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Var 2: {variable2Column ? `${variable2Prefix || ''}${variable2Column}${variable2Text || ''}` : <span className="italic text-gray-400">N/A</span>}</p>
                  </div>
                </div>
              )}
              {/* Show MapGenerationLoader overlay for heatmap/dotdensity, simple indicator for others */}
              {isSaving && (mapType === 'heatmap' || mapType === 'dotdensity') && (
                <MapGenerationLoader
                  isVisible={true}
                  mapType={mapType}
                  currentStage={1}
                  totalStages={5}
                  areaCount={viewAreaCount}
                />
              )}
              {isSaving && mapType !== 'heatmap' && mapType !== 'dotdensity' && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-blue-700 dark:text-blue-300 font-medium">Saving map configuration...</span>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Enhanced footer navigation that adapts to workflow */}
        <div className="px-6 py-4 bg-gray-100 dark:bg-gray-700/60 flex justify-between rounded-b-lg">
          <button 
            onClick={step > 1 ? prevStep : onCancel} 
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md" 
            disabled={isSaving}
          >
            {step > 1 ? 'Back' : 'Cancel'}
          </button>
          
          <button 
            onClick={step < maxSteps ? nextStep : handleCreateMap} 
            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md shadow-sm flex items-center ${isSaving ? 'opacity-75 cursor-not-allowed' : ''}`} 
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating & Saving...
              </>
            ) : (
              step < maxSteps ? 'Next' : 'Create Map'
            )}
          </button>
        </div>
      </div>
    </div>
    </>
  );
};

export default NewMapDialog;